import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { registerPlugin, Capacitor } from "@capacitor/core";
import {
  getGeminiKey,
  saveGeminiKey,
  getGeminiModel,
  saveGeminiModel,
  testGemini,
  GEMINI_MODELS,
  callGeminiChat,
  callGeminiVision,
  callGeminiChatStream,
  callGeminiVisionStream,
} from "./geminiAI";
import { isDictationAvailable, startDictation, sanitizeError, isPermissionError, openIOSSettings } from '../dictate';
import { useHayahPrefs } from "./hayahPrefs";
import HayahAIInsights from "./HayahAIInsights";
import { hapticTap, hapticConfirm, hapticHayahSuccess, hapticHayahSelection, hapticHayahToggle, hapticError } from "../hayahHaptics";
import { VoiceMode } from "../VoiceMode";

// v2 AI architecture (see HAYAH.md §AI):
//   - Stable systemInstruction (persona + bio + rules), built once per turn
//     but identical across turns of a conversation so Gemini's implicit
//     context caching can hit.
//   - Per-turn user-content context built fresh via buildCoachContext —
//     relevance-classified subset of the user's data.
//   - cards/followups via responseSchema, not <cards>/<followups> tags.
//   - Real streaming for text + vision via callGeminiChat/VisionStream,
//     with the v1 fake-stream replay as fallback when streaming fails.
import { COACH_VOICE, EVIDENCE_STANDARD, OUTPUT_PLAIN_TEXT, composeSystem } from "./hayah/hayahPrompts";
import { getUserProfile, formatProfileForPrompt } from "./hayah/userProfile";
import { readAllData, buildCoachContext } from "./hayah/aiContext";
import { EXERCISE_LIBRARY, coachWorkoutToTemplate, saveCoachWorkout, windDownWorkout } from "./riyadahData";
import CoachMarkdown from "./hayah/CoachMarkdown";
import { logFood, logWater, logWeight, logMounjaro, removeFoodEntries, resolveLocalLog, addFavoriteFood } from "./hayah/coachActions";
import { MODELS, costUSD } from "./hayah/aiConfig";
import { getUsageSummary, getLast7Days, resetUsage, seedBaselineIfNeeded } from "./hayah/aiTokenLog";
import HayahSwipeRow from "./hayah/SwipeRow";
import useSwipeBack from "./useSwipeBack";

// ── DATE / MATH HELPERS ───────────────────────────────────────────────────────

function localDateStr(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

// (v1 calcBMR moved to ./hayah/userProfile.js)

// ── DATA-ACCESS TOGGLES (persisted) ───────────────────────────────────────────

const DATA_DEFAULTS = { calories:true, weight:true, bf:true, sleep:true, steps:true, workout:true, fasting:true };

function loadAccess() {
  try {
    const stored = JSON.parse(localStorage.getItem("hayahCoachAccess")||"{}");
    // Migration: existing installs had `fasting:false` baked in (the old
    // default). Promote `false` → `true` so the user doesn't have to flip
    // the toggle manually to get the granular fasting context.
    if (stored && stored.fasting === false && localStorage.getItem("hayahCoachAccess:fastingMigrated") !== "1") {
      stored.fasting = true;
      try { localStorage.setItem("hayahCoachAccess:fastingMigrated", "1"); } catch {}
    }
    return { ...DATA_DEFAULTS, ...stored };
  } catch { return { ...DATA_DEFAULTS }; }
}
function saveAccess(a) { try { localStorage.setItem("hayahCoachAccess", JSON.stringify(a)); } catch {} }

// ── CONVERSATIONS (persisted history) ─────────────────────────────────────────

const HIST_KEY = "hayahCoachHistory";
const HIST_MAX = 5; // Keep only the most recent N conversations in History.
function loadConversations() {
  try { return JSON.parse(localStorage.getItem(HIST_KEY)||"[]").slice(0, HIST_MAX); } catch { return []; }
}
function saveConversations(list) {
  try { localStorage.setItem(HIST_KEY, JSON.stringify(list.slice(0, HIST_MAX))); } catch {}
}
function newId() { return Date.now().toString(36) + Math.random().toString(36).slice(2,7); }
const FB_KEY = "hayahCoachFeedback";
function loadFeedback() { try { return JSON.parse(localStorage.getItem(FB_KEY) || "{}") || {}; } catch { return {}; } }
function saveFeedback(m) { try { localStorage.setItem(FB_KEY, JSON.stringify(m)); } catch {} }
function inferTopic(messages) {
  const firstUser = messages.find(m => m.role === "user");
  if (!firstUser) return "New conversation";
  const t = firstUser.text.replace(/\s+/g, " ").trim();
  return t.length > 60 ? t.slice(0,57) + "…" : t;
}
function lastAssistantPreview(messages) {
  const last = [...messages].reverse().find(m => m.role === "ai");
  if (!last) return "";
  const t = last.text.replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
  return t.length > 110 ? t.slice(0,107) + "…" : t;
}
function relDate(ts) {
  const d = new Date(ts);
  const today = new Date(); today.setHours(0,0,0,0);
  const dDay = new Date(d); dDay.setHours(0,0,0,0);
  const diff = Math.round((today - dDay) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return d.toLocaleDateString(undefined, { weekday: "long" });
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// ── BUILD GEMINI SYSTEM PROMPT ───────────────────────────────────────────────
//
// v2: stable across all turns of a conversation — voice + bio + evidence +
// output rules. The dynamic data context (today/nutrition/recovery/etc) is
// built fresh per turn by buildCoachContext and prepended to the user
// message, not the system prompt. This lets Gemini's implicit context
// caching hit on the system prompt (~1.5KB) across turns.
function buildSystemInstruction() {
  const profile = getUserProfile();
  const profileBlock = formatProfileForPrompt(profile);
  return composeSystem([
    COACH_VOICE,
    profileBlock,
    EVIDENCE_STANDARD,
    OUTPUT_PLAIN_TEXT,
  ]);
}

// Structured-output schema for the AI Coach chat path. Replaces the v1
// <cards>...</cards> / <followups>...|...</followups> tag protocol with
// schema-enforced JSON. Cards were dropped 2026-05-24 — user found the
// data-graph affordances noisy; the schema now only carries reply + followups.
const COACH_CHAT_SCHEMA = {
  type: "object",
  properties: {
    reply: { type: "string" },
    followups: {
      type: "array",
      maxItems: 3,
      items: { type: "string" },
    },
    // Optional — populated ONLY when the user asks the Coach to build a
    // workout to do (and an EXERCISE LIBRARY is provided in context).
    workout: {
      type: "object",
      properties: {
        name: { type: "string" },
        note: { type: "string" },
        exercises: {
          type: "array",
          minItems: 1,
          maxItems: 8,
          items: {
            type: "object",
            properties: {
              id: { type: "string" },     // exact EXERCISE LIBRARY id
              sets: { type: "integer" },
              reps: { type: "integer" },
              weight: { type: "number" }, // kg; 0 = bodyweight
            },
            required: ["id"],
          },
        },
      },
      required: ["exercises"],
    },
    // Optional — log actions the Coach should take in the app. Populate ONLY
    // when the user asks to log/record/add/track something.
    actions: {
      type: "array",
      maxItems: 8,
      items: {
        type: "object",
        properties: {
          type: { type: "string", enum: ["logFood", "logWater", "logWeight", "logMounjaro"] },
          // logFood:
          slot: { type: "string" },   // meal | drinks | supplements | whey | snacks
          items: {
            type: "array",
            items: {
              type: "object",
              properties: {
                name: { type: "string" },
                calories: { type: "number" },
                protein: { type: "number" },
                carbs: { type: "number" },
                fat: { type: "number" },
                servingLabel: { type: "string" },
              },
              required: ["name"],
            },
          },
          ml: { type: "number" },   // logWater
          cups: { type: "number" }, // logWater (alt)
          kg: { type: "number" },   // logWeight
        },
        required: ["type"],
      },
    },
    actionButtons: {
      type: "array",
      maxItems: 3,
      items: {
        type: "object",
        properties: {
          kind: { type: "string", enum: ["logFood", "logWater", "quickAdd"] },
          label: { type: "string" },
          slot: { type: "string" },
          items: {
            type: "array",
            items: {
              type: "object",
              properties: {
                name: { type: "string" }, calories: { type: "number" }, protein: { type: "number" },
                carbs: { type: "number" }, fat: { type: "number" }, servingLabel: { type: "string" },
              },
              required: ["name"],
            },
          },
          ml: { type: "number" },
        },
        required: ["kind", "label"],
      },
    },
  },
  required: ["reply", "followups"],
};

// Same schema but with `actions` REQUIRED — used on log-intent turns so the
// model can't silently answer in prose without emitting the structured action
// (the bug: "I've logged it" with nothing actually saved). Gemini reliably
// fills required fields; it kept skipping the optional one.
const COACH_CHAT_SCHEMA_LOG = {
  ...COACH_CHAT_SCHEMA,
  properties: {
    ...COACH_CHAT_SCHEMA.properties,
    // minItems:1 forces a NON-EMPTY action — "required" alone let the model
    // satisfy the schema with actions:[] and answer in prose (nothing logged).
    actions: {
      ...COACH_CHAT_SCHEMA.properties.actions,
      minItems: 1,
      items: {
        ...COACH_CHAT_SCHEMA.properties.actions.items,
        // logFood actions must carry ≥1 food item.
        properties: {
          ...COACH_CHAT_SCHEMA.properties.actions.items.properties,
          items: { ...COACH_CHAT_SCHEMA.properties.actions.items.properties.items, minItems: 1 },
        },
      },
    },
  },
  required: ["reply", "followups", "actions"],
};

// Detect a request to LOG/record something the user consumed or measured.
function wantsToLog(text) {
  const t = String(text || "").trim();
  // 1. Imperative record commands — always treat as logging.
  if (/^\s*(log|add|record|track|note)\b/i.test(t)) return true;          // "log 50g biltong"
  if (/\b(log|record|track)\s+(this|that|it|the following)\b/i.test(t)) return true;
  // 2. Questions / ANALYSIS about existing data — NOT a request to record.
  //    "should I…", "analyze my log", "what did I eat", "today's calorie log".
  //    These must be caught BEFORE the "I ate…" rule (else "what I ate today"
  //    is mistaken for a food report and the coach asks what to log).
  if (/\b(analys|analyz|review|assess|summari[sz]e|breakdown|how (?:much|many|am i|is|are)|should i|what (?:did|have|do|should)|what'?s|whats|see (?:all|what)|show me|look at|tell me|on track|am i (?:on|hitting|over|under|getting|doing)|enough|too (?:much|many|little))\b/i.test(t)) return false;
  if (/\b(?:today'?s|todays|daily|my|the|calorie|food)\b[^.?!]{0,12}\blog\b/i.test(t)) return false; // "today's calorie log", "in my log"
  // 3. Past-tense intake report → log.
  if (/\bi (?:just )?(?:had|ate|drank|took|have eaten|have had)\b/i.test(t)) return true;
  // 4. Fallback — bare log/record/track verb, or "add … <intake noun>".
  if (/\b(log|record|track)\b/i.test(t)) return true;
  if (/\badd\b[^.?!]{0,30}\b(diary|log|intake|water|weight|calories|kcal|protein|meal)\b/i.test(t)) return true;
  return false;
}

// Extra directive on log-intent turns: forces a populated action + shows the
// exact JSON shape (a concrete example is the single biggest lever for getting
// the model to emit structured output reliably).
const LOG_DIRECTIVE = `THE USER IS ASKING YOU TO LOG SOMETHING. You MUST return the "actions" array POPULATED (not empty) with the matching action — never claim in "reply" that you logged something without the action, or nothing is saved. Pick the slot (meal=main food, drinks=tea/coffee/juice/soft drinks, supplements=creatine/vitamins, whey=protein shakes, snacks=treats), estimate macros for clear foods.
EXAMPLE — user: "log a cappuccino" →
{"reply":"Logged your cappuccino — +38 kcal.","followups":["What's my protein now?","How many calories left?","What should I eat next?"],"actions":[{"type":"logFood","slot":"drinks","items":[{"name":"Cappuccino","calories":38,"protein":2,"carbs":4,"fat":1,"servingLabel":"1 cup"}]}]}
Only return actions:[] if the message genuinely isn't a request to record food/water/weight.`;

// Semantic guidance to accompany the schema. The schema enforces shape;
// this rule tells the model what each field means.
const COACH_CHAT_OUTPUT_RULE = `Output a JSON object with fields: reply, followups (and optionally workout).
- reply: your answer text. Apply the voice and output rules above. When the user asks for a "table" / "list" / "ranking" of multiple items (PRs, exercises, meals, sleep nights), render it as a GitHub-flavoured markdown table inside the reply string (escape newlines as \\n). Tables are far more scannable than semicolon-separated prose.
- followups: 3 short follow-up questions the user might naturally ask next, each under 40 characters.
- workout: OMIT entirely unless the user explicitly asks you to build/create a workout to do AND an "EXERCISE LIBRARY" block is present in the context. When you do build one, pick exercises by EXACT id from that library only.
- actions: OMIT unless the user asks you to LOG / RECORD / ADD / TRACK something in the app. Each action does a real write:
  • logFood — they ate/drank/took something. Set "slot" (meal=main food, drinks=tea/coffee/juice, supplements=creatine/pre-workout/vitamins, whey=protein shakes, snacks=junk/treats) and "items" with a realistic macro ESTIMATE per item (calories, protein, carbs, fat, servingLabel) from your nutrition knowledge + their region/portion. One item per distinct food.
  • logWater — give "ml" (or "cups"; 1 cup = 250 ml).
  • logWeight — give "kg".
  • logMounjaro — they took their weekly Mounjaro/GLP-1 dose (no other fields).
- actionButtons: OPTIONAL, up to 3. When you RECOMMEND something (vs the user asking to log) — e.g. "you could have a protein shake", "drink some water", "save this meal" — surface it as a tappable button instead of auto-logging. Each: { kind:"logFood"|"logWater"|"quickAdd", label (short, e.g. "Log a whey shake"), and for logFood/quickAdd a "slot" + "items" (macro estimates), for logWater an "ml" }. These do NOT save until the user taps. Use actionButtons for recommendations; use "actions" only when they explicitly say to log.
  EXAMPLE — "what can I have to hit my protein?" -> reply explains, then actionButtons:[{"kind":"logFood","label":"Log a whey shake","slot":"whey","items":[{"name":"Gold Standard Whey","calories":120,"protein":24,"carbs":3,"fat":1,"servingLabel":"1 scoop (30g)"}]}]
  CRITICAL for logFood actionButtons: you MUST include "items" with at least one food item with realistic non-zero macros — otherwise nothing saves. The label alone is not enough.
  CRITICAL: the ONLY thing that actually saves data is the structured "actions" array. If you tell the user in "reply" that you've logged/added something, you MUST also include the matching action — a reply that says "I've logged it" with no action saves NOTHING and is a bug. So: include the action AND a short confirming reply, or, if you're genuinely unsure what they ate (and choose not to estimate), ask in the reply and emit NO action and do NOT claim you logged it. A sensible estimate is expected for clear foods. All logs are applied immediately (the user can undo), so "I've logged your X" is accurate when you include the action.`;

// Compact exercise library + builder instruction, appended to the turn context
// only when the user wants a workout (keeps the stable system prompt cacheable
// and avoids paying for 150 ids every turn).
function workoutBuilderContext() {
  const lib = EXERCISE_LIBRARY.map(e => `${e.id} | ${e.name} | ${e.primary} | ${e.unit}`).join("\n");
  return `WORKOUT BUILDER — the user wants a workout to do. Populate the optional "workout" field: { name, note (one line), exercises:[{ id, sets, reps, weight }] }. Pick 5–8 exercises by EXACT id from the library below. Realistic sets/reps; weight in kg (0 = bodyweight).

PRIORITISE WHAT THE USER SAID IN THIS CONVERSATION over the objective data — how they feel, any pain/injury, time available, equipment on hand, energy level, and the body part / style they asked for. These OVERRIDE readiness/progression when they conflict:
- Pain/injury ("knee's sore", "shoulder tight") → avoid exercises that load it, even if that muscle reads fresh/under-trained (sore knee → no leg press / squats / lunges; pick hamstring/glute/upper instead).
- Short on time → fewer exercises, shorter session.
- "Keep it light / easy / I'm tired / drained" → reduce load and volume, no PRs; lean toward steadier or aerobic work.
- Requested focus (legs, push, cardio…) or equipment (dumbbells only, bodyweight) → honour it strictly.
If what they told you means they shouldn't train hard, scale the session down and say so in "reply".

Otherwise use their recent training, progression and readiness from the context above and avoid muscles trained in the last 24h. Keep "reply" to a one-line intro and tell them to tap "Open in Exercise" to start it.
EXERCISE LIBRARY (id | name | muscle | unit):\n${lib}`;
}

// Intent match for "build me a workout to do". Also fires on a terse
// confirmation ("yes", "build it", "go for it") when the recent conversation
// has been about training — so a chat that lands on "ok build it" still works.
function wantsWorkoutBuild(text, recentMsgs = []) {
  const t = String(text || "").trim();
  const recent = (recentMsgs || []).slice(-8).map(m => m && m.text ? m.text : "").join(" ");
  const recentAboutTraining = /\b(workout|session|routine|train(?:ing|ed)?|exercise|gym|lift|reps?|sets?|legs?|push|pull|cardio|squat|bench|deadlift|plank|stretch|core|lower back|glute|mobility|bodyweight|body weight)\b/i.test(recent);

  // Explicit "open in exercise" / "open it in the exercise tab" — unambiguous
  // request to get the workout in an openable form. This is the single
  // clearest signal, so honour it regardless of phrasing.
  if (/\bopen\b[^.?!]{0,25}\bexercise\b/i.test(t)) return true;

  // Direct build requests naming a workout-ish object (now includes
  // "version"/"one"/"circuit"/"program" so "give me a home version" counts).
  if (/\b(build|create|make|generate|give me|gimme|plan|put together|design|set me up|send me|write me)\b[^.?!]{0,45}\b(workout|session|routine|train(?:ing)?|exercises?|circuit|program|plan|version|one)\b/i.test(t)) return true;
  if (/\bwhat should i (?:train|do)\b[^.?!]{0,30}\b(today|now)\b/i.test(t)) return true;
  if (/\b(workout|session)\b[^.?!]{0,20}\b(today|now|for me)\b/i.test(t)) return true;

  // Follow-ups that only make sense mid workout-chat: "a home version",
  // "bodyweight version", "an easier one", "give it to me", "I can do at home".
  if (recentAboutTraining) {
    if (/\b(home|body ?weight|dumbbell|kettlebell|gym|easier|lighter|harder|shorter|longer|quick|beginner)\b[^.?!]{0,30}\b(version|workout|routine|session|one|circuit)\b/i.test(t)) return true;
    if (/\b(i can do|do at home|do at the gym|without (?:weights|equipment)|with (?:body ?weights?|dumbbells?|just|only))\b/i.test(t)) return true;
    if (/\b(give|send|make|build|create)\b[^.?!]{0,20}\b(it|me|that|one|this)\b/i.test(t)) return true;
  }

  // Modify-the-current-workout follow-ups: "make it 2 sets", "leave out planks",
  // "swap the lunges", "without the squats", "add a stretch", "shorter". These
  // only make sense when a workout is already on the table — and they MUST
  // re-inject the library, else the coach says "library not available".
  if (recentAboutTraining && (
    /\b(leave out|take out|remove|drop|swap|replace|without|instead of|fewer|more|less|add (?:a|an|in|more)|change|make it|just (?:do|have)|only (?:do|have)|reduce|increase|shorter|longer|easier|harder|lighter|heavier)\b/i.test(t)
    || /\b\d+\s*(sets?|reps?|rounds?|exercises?|min)\b/i.test(t)
  )) return true;

  // Terse confirmation / build verb — only when the recent chat mentions training.
  const terse = t.length <= 40;
  const confirm = /^(yes|yep|yeah|sure|ok(ay)?|please|go|go ahead|go for it|do it|build it|make it|set it up|let'?s go|sounds good|that works)\b/i.test(t)
    || /\b(build|make|create|generate|put together|set (?:it|that) up|do (?:it|that))\b/i.test(t);
  if (terse && confirm && recentAboutTraining) return true;
  return false;
}

// Calming bedtime / relaxation request → launch the gentle wind-down routine.
function wantsWindDown(text) {
  const t = String(text || "");
  if (/\b(wind[\s-]?down|unwind|decompress|loosen up|night[\s-]?routine|bed[\s-]?time (?:stretch|routine|wind))\b/i.test(t)) return true;
  if (/\bstretch(?:es|ing)?\b/i.test(t) && /\b(bed|sleep|night|relax|tonight|wind|before bed|in bed|calm)\b/i.test(t)) return true;
  if (/\b(help me|something to|want to|need to|like to)\b[^.?!]{0,30}\b(relax|sleep|unwind|wind down|calm down)\b/i.test(t)) return true;
  if (/\b(relax|calm)\b[^.?!]{0,25}\b(before bed|to sleep|for sleep|tonight|in bed|at night)\b/i.test(t)) return true;
  return false;
}

// Apply the Coach's log actions. ALL write immediately so the Coach never
// claims "logged" while nothing was saved — food carries an `undo` payload so
// the user can reverse a wrong estimate (this replaces the old confirm card,
// which left "I've logged it" replies with nothing actually written).
// Returns { applied: [{label, undo?}] | null }.
function applyCoachActions(actions) {
  const applied = [];
  for (const a of (actions || [])) {
    if (!a || !a.type) continue;
    try {
      if (a.type === "logFood") {
        const items = Array.isArray(a.items) ? a.items.filter(it => it && it.name) : [];
        if (!items.length) continue;
        const r = logFood(a.slot || "meal", items);
        if (r.count) applied.push({
          label: `🍽️ Logged ${items.map(it => it.name).join(", ")}${r.kcal ? ` · +${r.kcal} kcal` : ""}`,
          undo: { slot: r.slot, ids: r.ids },
        });
      } else if (a.type === "logWater") {
        const r = logWater({ ml: a.ml, cups: a.cups });
        if (r.cups) applied.push({ label: `💧 Water logged · ${r.cups} ${r.cups === 1 ? "cup" : "cups"} today` });
      } else if (a.type === "logWeight") {
        const r = logWeight(a.kg);
        if (r) applied.push({ label: `⚖️ Weight logged · ${r.kg} kg` });
      } else if (a.type === "logMounjaro") {
        logMounjaro();
        applied.push({ label: "💉 Mounjaro dose logged" });
      }
    } catch {}
  }
  return { applied: applied.length ? applied : null };
}

// ── TODAY-AT-A-GLANCE (for empty/home tiles) ──────────────────────────────────

function todayStats() {
  const today = localDateStr();
  let calorieLog = {}; try { calorieLog = JSON.parse(localStorage.getItem("calorieLog")||"{}"); } catch {}
  let goals = { calories:2000, protein:150, sleep:8, steps:10000 };
  try { goals = {...goals, ...JSON.parse(localStorage.getItem("calorieGoals")||"{}")}; } catch {}
  let weightLog = []; try { weightLog = JSON.parse(localStorage.getItem("calorieWeightLog")||"[]"); } catch {}
  const todayLog = calorieLog[today] || {};
  const items = Object.values(todayLog.meals||{}).flat();
  const cal = Math.round(items.reduce((s,e)=>s+(e.calories||0),0));
  const prot = Math.round(items.reduce((s,e)=>s+(e.protein||0),0));
  const burned = Math.round((todayLog.caloriesBurned||0) + (todayLog.basalBurned||0));
  const sleep = todayLog.sleepMinutes || 0;
  const steps = todayLog.steps || 0;
  const days7 = Array.from({length:7}, (_,i) => localDateStr(-(6-i)));
  const logged7 = days7.filter(dt => {
    const l = calorieLog[dt];
    return l && Object.values(l.meals||{}).flat().length > 0;
  });
  const avg7 = (days, fn) => {
    const vals = days.map(dt => fn(calorieLog[dt])).filter(v => v > 0);
    return vals.length ? Math.round(vals.reduce((s,v)=>s+v,0)/vals.length) : 0;
  };
  const avg7Cal = avg7(logged7, d => Object.values(d.meals||{}).flat().reduce((s,e)=>s+(e.calories||0),0));
  const avg7Prot = avg7(logged7, d => Object.values(d.meals||{}).flat().reduce((s,e)=>s+(e.protein||0),0));
  const sleepDays = logged7.filter(dt => (calorieLog[dt].sleepMinutes||0) > 0);
  const avg7Sleep = avg7(sleepDays, d => d.sleepMinutes||0);
  let streak = 0;
  for (let i = 0; i < 60; i++) {
    const dt = localDateStr(-i);
    const l = calorieLog[dt];
    if (l && Object.values(l.meals||{}).flat().length > 0) streak++; else break;
  }
  return {
    cal, calGoal: goals.calories || 2000,
    prot, protGoal: goals.protein || 150,
    sleep, sleepGoal: goals.sleep || 8,
    steps, stepGoal: goals.steps || 10000,
    burned, avg7Cal, avg7Prot, avg7Sleep,
    streak,
    hasData: cal > 0 || steps > 0 || sleep > 0 || prot > 0,
    weightLast: weightLog.length ? weightLog[weightLog.length-1].kg : null,
  };
}

// ── QUICK-ACTION STATS (remaining macros + recovery + last workout) ──────────

function hayahQuickStats() {
  const today = localDateStr();
  let calorieLog = {}; try { calorieLog = JSON.parse(localStorage.getItem("calorieLog")||"{}"); } catch {}
  let goals = { calories:2000, protein:150, carbs:225, fat:65 };
  try { goals = {...goals, ...JSON.parse(localStorage.getItem("calorieGoals")||"{}")}; } catch {}
  let workouts = []; try { workouts = JSON.parse(localStorage.getItem("sirat-workouts")||"[]"); } catch {}

  const todayLog = calorieLog[today] || {};
  const items = Object.values(todayLog.meals||{}).flat();
  const cal   = Math.round(items.reduce((s,e)=>s+(e.calories||0),0));
  const prot  = Math.round(items.reduce((s,e)=>s+(e.protein ||0),0));
  const carbs = Math.round(items.reduce((s,e)=>s+(e.carbs   ||0),0));
  const fat   = Math.round(items.reduce((s,e)=>s+(e.fat     ||0),0));

  const sleep = todayLog.sleepMinutes || 0;
  const hrv   = todayLog.heartRateVariability || 0;
  const rhr   = todayLog.restingHeartRate || 0;

  // Recovery score 1-10 — simple weighted blend of sleep duration and HRV.
  let recovery = 5;
  if (sleep > 0) {
    const h = sleep / 60;
    if      (h >= 7.5) recovery += 2.5;
    else if (h >= 6.5) recovery += 1.5;
    else if (h >= 5.5) recovery += 0.5;
    else if (h <  5)   recovery -= 2;
    else               recovery -= 0.5;
  }
  if (hrv > 0) {
    if      (hrv >= 50) recovery += 1.5;
    else if (hrv >= 35) recovery += 0.5;
    else if (hrv <  25) recovery -= 1;
  }
  if (rhr > 0 && rhr > 70) recovery -= 0.5;
  recovery = Math.max(1, Math.min(10, Math.round(recovery)));
  const recoveryConfident = sleep > 0 || hrv > 0;

  // Last workout (filters out undated imports same as buildContext does)
  const now = Date.now();
  const fiveYrsAgo = now - 5*365*86400000;
  const wTs = w => {
    const ts = w.timestamp || w.completedAt || w.startedAt || 0;
    if (ts > 0) return ts;
    if (w.date) { const d = Date.parse(`${w.date}T12:00:00`); if (!isNaN(d)) return d; }
    return 0;
  };
  const validW = workouts.filter(w => { const ts = wTs(w); return ts > fiveYrsAgo && ts <= now; });
  const sortedW = [...validW].sort((x,y) => wTs(y) - wTs(x));
  const lastW = sortedW[0];
  const lastWorkoutDaysAgo = lastW ? Math.floor((now - wTs(lastW)) / 86400000) : null;

  return {
    calRemaining:   Math.max(0, (goals.calories || 2000) - cal),
    protRemaining:  Math.max(0, (goals.protein  || 150)  - prot),
    carbsRemaining: Math.max(0, (goals.carbs    || 225)  - carbs),
    fatRemaining:   Math.max(0, (goals.fat      || 65)   - fat),
    recovery, recoveryConfident,
    lastWorkoutDaysAgo,
    lastWorkoutName: lastW?.name || null,
    timeOfDay: (() => {
      const h = new Date().getHours();
      if (h < 5)  return "late night";
      if (h < 11) return "breakfast";
      if (h < 15) return "lunch";
      if (h < 18) return "afternoon";
      if (h < 22) return "dinner";
      return "evening";
    })(),
  };
}

// ── COMPUTE INSIGHT CARDS (Home view) ─────────────────────────────────────────

function computeInsights(s, access, C) {
  const cards = [];
  if (access.sleep && s.sleep > 0) {
    const sleepH = s.sleep / 60;
    const goalH = s.sleepGoal;
    const debt = goalH - sleepH;
    if (debt > 0.5) {
      cards.push({
        id: "sleep", tag: "SLEEP", color: C.coral,
        headline: "Short sleep → hunger spike",
        body: `${Math.floor(s.sleep/60)}h ${s.sleep%60}m last night. Ghrelin rises with sleep debt — expect ${Math.round(debt*150)}–${Math.round(debt*200)} kcal extra hunger pressure today.`,
        stat: `${Math.floor(s.sleep/60)}h ${s.sleep%60}m`, sub: `target ${goalH}h`,
        prompt: "How does my sleep affect me?",
      });
    } else if (debt <= 0) {
      cards.push({
        id: "sleep", tag: "SLEEP", color: C.sage,
        headline: "Sleep on target",
        body: `${Math.floor(s.sleep/60)}h ${s.sleep%60}m last night — meeting the ${goalH}h goal. Recovery and hunger hormones are well supported.`,
        stat: `${Math.floor(s.sleep/60)}h`, sub: `target ${goalH}h`,
        prompt: "Is my sleep affecting my progress?",
      });
    }
  }
  if (access.calories && s.avg7Prot > 0) {
    const pct = s.protGoal ? Math.round((s.avg7Prot / s.protGoal) * 100) : 0;
    if (pct < 85) {
      cards.push({
        id: "protein", tag: "PROTEIN", color: C.amber,
        headline: "Protein under target this week",
        body: `Hitting ${pct}% of your ${s.protGoal}g protein target on average. Muscle protein synthesis needs consistency — small daily gaps add up.`,
        stat: `${pct}%`, sub: `${s.protGoal}g goal`,
        prompt: "Am I eating enough protein?",
      });
    }
  }
  if (access.calories && s.avg7Cal > 0) {
    const diff = Math.abs(s.avg7Cal - s.calGoal);
    const pct = s.calGoal ? (diff / s.calGoal) * 100 : 100;
    if (pct < 5) {
      cards.push({
        id: "calories", tag: "CALORIES", color: C.sage,
        headline: "Weekly average is on track",
        body: `7-day avg ${s.avg7Cal.toLocaleString()} kcal vs ${s.calGoal.toLocaleString()} target. Within ${pct.toFixed(1)}%. Stay consistent.`,
        stat: s.avg7Cal.toLocaleString(), sub: `${s.calGoal.toLocaleString()} goal`,
        prompt: "Am I on track today?",
      });
    } else {
      cards.push({
        id: "calories", tag: "CALORIES", color: s.avg7Cal > s.calGoal ? C.coral : C.amber,
        headline: s.avg7Cal > s.calGoal ? "Over your weekly target" : "Under your weekly target",
        body: `7-day avg ${s.avg7Cal.toLocaleString()} kcal vs ${s.calGoal.toLocaleString()} goal. ${s.avg7Cal > s.calGoal ? `You're ${(s.avg7Cal - s.calGoal).toLocaleString()} kcal/day over.` : `You're running ${(s.calGoal - s.avg7Cal).toLocaleString()} kcal/day under.`}`,
        stat: s.avg7Cal.toLocaleString(), sub: `${s.calGoal.toLocaleString()} goal`,
        prompt: "Am I on track today?",
      });
    }
  }
  return cards;
}

// ── MARKDOWN BOLD ─────────────────────────────────────────────────────────────

function parseBold(text) {
  const parts = [];
  const re = /\*\*(.*?)\*\*/g;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ t:'text', c:text.slice(last, m.index) });
    parts.push({ t:'bold', c:m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ t:'text', c:text.slice(last) });
  return parts;
}

function BoldText({ text, boldColor }) {
  return parseBold(text).map((p, i) =>
    p.t === 'bold'
      ? <strong key={i} style={{ color: boldColor, fontWeight: 600 }}>{p.c}</strong>
      : <span key={i}>{p.c}</span>
  );
}

// RichBody — like BoldText but also renders GFM markdown tables as real
// HTML tables. The Coach is now instructed to use tables for any "list /
// ranking / table of" requests (PRs, last N exercises, etc.). Splits the
// input into segments: contiguous table blocks become <table>, everything
// else flows through BoldText so **bold** still works.
//
// Table format:
//   | Col A | Col B |
//   | ----- | ----- |
//   | data  | data  |
//
// Cells inside the table also get bold parsing so **90kg x 15** highlights.
function RichBody({ text, boldColor, accent }) {
  const lines = String(text || "").split("\n");
  const segments = []; // array of { type:'text'|'table', lines:[] }
  let cur = null;
  const isTableLine = (l) => /^\s*\|.*\|\s*$/.test(l);
  const isSeparator = (l) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(l);
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    const next = lines[i + 1] || "";
    // A header row followed by a separator row starts a table.
    if (isTableLine(l) && isSeparator(next)) {
      if (cur && cur.type !== 'table') { segments.push(cur); cur = null; }
      if (!cur) cur = { type: 'table', lines: [] };
      cur.lines.push(l);
      cur.lines.push(next);
      i++; // consumed separator
      // Pull in any following body rows.
      while (i + 1 < lines.length && isTableLine(lines[i + 1])) {
        i++;
        cur.lines.push(lines[i]);
      }
      segments.push(cur);
      cur = null;
      continue;
    }
    if (!cur || cur.type !== 'text') { if (cur) segments.push(cur); cur = { type: 'text', lines: [] }; }
    cur.lines.push(l);
  }
  if (cur) segments.push(cur);

  const splitCells = (row) =>
    row.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map(c => c.trim());

  return segments.map((seg, si) => {
    if (seg.type === 'table') {
      const header = splitCells(seg.lines[0]);
      const body = seg.lines.slice(2).map(splitCells);
      return (
        <div key={si} style={{ overflowX: 'auto', margin: '8px 0 10px', WebkitOverflowScrolling: 'touch' }}>
          <table style={{
            borderCollapse: 'collapse',
            width: '100%',
            fontSize: 12.5,
            fontFamily: 'inherit',
            lineHeight: 1.45,
          }}>
            <thead>
              <tr>
                {header.map((h, hi) => (
                  <th key={hi} style={{
                    textAlign: 'left',
                    padding: '6px 10px',
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                    fontSize: 9.5,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: accent || boldColor,
                    fontWeight: 700,
                    borderBottom: `0.5px solid ${accent || boldColor}33`,
                    whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{
                      padding: '7px 10px',
                      borderBottom: ri === body.length - 1 ? 'none' : '0.5px solid rgba(255,255,255,0.06)',
                      verticalAlign: 'top',
                      whiteSpace: 'nowrap',
                    }}>
                      <BoldText text={cell} boldColor={boldColor}/>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    // Text segment — preserve internal line breaks, run through BoldText.
    return (
      <span key={si} style={{ whiteSpace: 'pre-wrap' }}>
        <BoldText text={seg.lines.join("\n")} boldColor={boldColor}/>
      </span>
    );
  });
}

// ── COACH ORB ─────────────────────────────────────────────────────────────────

function CoachOrb({ size = 36, pulse = false, C }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `radial-gradient(circle at 35% 35%, ${C.amberS}, ${C.amber} 55%, #A06010)`,
      boxShadow: pulse
        ? `0 0 0 6px ${C.amberD}, 0 0 20px rgba(224,160,48,0.3)`
        : `0 2px 8px rgba(224,160,48,0.25)`,
      animation: pulse ? 'haic-orb 2s ease-in-out infinite' : 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ width: size*0.38, height: size*0.38, borderRadius: '50%', background: 'rgba(255,255,255,0.25)' }}/>
    </div>
  );
}

// ── SPARK BARS ────────────────────────────────────────────────────────────────

function SparkBars({ data, color, goal, h=20, barW=4, gp=2 }) {
  if (!data || !data.length) return null;
  const max = Math.max(...data, goal||0) * 1.1 || 1;
  const total = data.length*(barW+gp)-gp;
  return (
    <svg width={total} height={h} viewBox={`0 0 ${total} ${h}`} style={{ flexShrink:0 }}>
      {data.map((v,i)=>{
        const bh = Math.max(2, Math.round((v/max)*h));
        return <rect key={i} x={i*(barW+gp)} y={h-bh} width={barW} height={bh} rx={1.5}
          fill={color} opacity={i===data.length-1 ? 1 : 0.28 + (i/(data.length-1))*0.4}/>;
      })}
      {goal && <line x1={0} y1={h-Math.round((goal/max)*h)} x2={total} y2={h-Math.round((goal/max)*h)}
        stroke={color} strokeWidth={0.8} strokeDasharray="2 2" opacity={0.65}/>}
    </svg>
  );
}

// ── CAMERA + CARD HELPERS ────────────────────────────────────────────────────

const CapacitorCamera = registerPlugin('Camera');

// Fallback Hayah goals when localStorage.calorieGoals is missing.
// Mirrors HAYAH_DEFAULT_GOALS in CalorieCounter.js so the chat cards
// have sane thresholds for first-time users.
const HAIC_DEFAULT_GOALS = {
  calories: 2000, protein: 150, carbs: 225, fat: 65,
  steps: 12000, burn: 3000, sleep: 8, workouts: 3,
};

// Topic regex — case-insensitive, anchored with \b where possible to avoid
// false positives like "step back" matching steps. Score = match count;
// topics with score 0 don't surface a card.
const HAIC_TOPIC_REGEX = {
  sleep:    /sleep|bedtime|\bREM\b|deep sleep|rested|insomnia|nap/gi,
  calories: /\b(calor|kcal|deficit|surplus|intake|burned?)\w*/gi,
  protein:  /\b(protein|amino|whey|lean mass)\b/gi,
  weight:   /\b(weight|kg|lbs?|scale|body fat|body comp)\b/gi,
  steps:    /\b(steps?|walk(?:ed|ing)?|stand hours|distance)\b/gi,
  workout:  /\b(workout|train(?:ed|ing)?|gym|sets?|reps?|lift(?:ed|ing)?)\b/gi,
};

function detectTopics(text, max = 3) {
  if (!text) return [];
  const scores = {};
  for (const [topic, re] of Object.entries(HAIC_TOPIC_REGEX)) {
    const matches = String(text).match(re);
    if (matches && matches.length) scores[topic] = matches.length;
  }
  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, max)
    .map(([t]) => t);
}

// Compute 7-day card payloads for the requested topics. Reads calorieLog,
// calorieGoals, and sirat-workouts straight from localStorage so we don't
// depend on CalorieCounter being mounted. Honors data-access toggles —
// a card is suppressed if its underlying source is gated off in Data view.
function compute7DayCards(topics, access, C) {
  if (!topics || !topics.length) return [];
  let log = {};
  try { log = JSON.parse(localStorage.getItem('calorieLog') || '{}'); } catch {}
  let goals = HAIC_DEFAULT_GOALS;
  try { goals = { ...HAIC_DEFAULT_GOALS, ...(JSON.parse(localStorage.getItem('calorieGoals') || '{}')) }; } catch {}
  let workouts = [];
  try { workouts = JSON.parse(localStorage.getItem('sirat-workouts') || '[]'); } catch {}

  const days = [];
  for (let i = 6; i >= 0; i--) days.push(localDateStr(-i));

  const workoutDates = new Set();
  for (const w of workouts) {
    const ts = w.timestamp || w.completedAt || w.startedAt;
    if (!ts) continue;
    try {
      const d = new Date(ts);
      workoutDates.add(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
    } catch {}
  }

  const accessGate = {
    sleep: access?.sleep, calories: access?.calories, protein: access?.calories,
    weight: access?.weight, steps: access?.steps, workout: access?.workout,
  };

  const out = [];
  for (const t of topics) {
    if (accessGate[t] === false) continue;
    let data = [], goal = 0, current = 0, unit = '', label = '', color = C.amber, trend = '';
    if (t === 'sleep') {
      data = days.map(d => Math.round(((log[d]?.sleepMinutes || 0) / 60) * 10) / 10);
      goal = goals.sleep || 8;
      current = data[data.length - 1] || 0;
      const logged = data.filter(v => v > 0);
      const below = data.filter(v => v > 0 && v < goal).length;
      trend = logged.length === 0 ? 'No sleep data yet' : below === 0 ? 'On target all week' : `Below target ${below} day${below === 1 ? '' : 's'}`;
      label = 'SLEEP'; color = C.coral; unit = 'h';
    } else if (t === 'calories') {
      data = days.map(d => {
        const m = log[d]?.meals || {};
        return Math.round(Object.values(m).flat().reduce((sum, e) => sum + (e.calories || 0), 0));
      });
      goal = goals.calories || 2000;
      current = data[data.length - 1] || 0;
      const logged = data.filter(v => v > 0);
      const avg = logged.length ? Math.round(logged.reduce((s, v) => s + v, 0) / logged.length) : 0;
      const delta = current - avg;
      trend = !logged.length ? 'No calories logged' : Math.abs(delta) < 75 ? 'On track this week' : `${delta > 0 ? '+' : ''}${delta} kcal vs avg`;
      label = 'CALORIES'; color = C.amber; unit = 'kcal';
    } else if (t === 'protein') {
      data = days.map(d => {
        const m = log[d]?.meals || {};
        return Math.round(Object.values(m).flat().reduce((sum, e) => sum + (e.protein || 0), 0));
      });
      goal = goals.protein || 150;
      current = data[data.length - 1] || 0;
      const logged = data.filter(v => v > 0);
      const below = data.filter(v => v > 0 && v < goal).length;
      trend = !logged.length ? 'No protein logged' : below === 0 ? 'On target all week' : `Below target ${below} day${below === 1 ? '' : 's'}`;
      label = 'PROTEIN'; color = C.amber; unit = 'g';
    } else if (t === 'weight') {
      data = days.map(d => Math.round((log[d]?.weight || 0) * 10) / 10);
      const filled = data.filter(v => v > 0);
      current = filled[filled.length - 1] || 0;
      goal = 0;
      const first = filled[0] || 0, last = filled[filled.length - 1] || 0;
      const delta = (first && last) ? +(last - first).toFixed(1) : 0;
      trend = !filled.length ? 'No weight logged' : Math.abs(delta) < 0.1 ? 'Steady' : `${delta > 0 ? '+' : ''}${delta} kg over 7 days`;
      label = 'WEIGHT'; color = C.sage; unit = 'kg';
    } else if (t === 'steps') {
      data = days.map(d => log[d]?.steps || 0);
      goal = goals.steps || 12000;
      current = data[data.length - 1] || 0;
      const logged = data.filter(v => v > 0);
      const below = data.filter(v => v > 0 && v < goal).length;
      trend = !logged.length ? 'No step data yet' : below === 0 ? 'On target all week' : `Below target ${below} day${below === 1 ? '' : 's'}`;
      label = 'STEPS'; color = C.sage; unit = 'steps';
    } else if (t === 'workout') {
      data = days.map(d => workoutDates.has(d) ? 1 : 0);
      const week = data.reduce((sum, v) => sum + v, 0);
      goal = goals.workouts || 3;
      current = week;
      trend = week >= goal ? `${week} sessions this week` : `${week} of ${goal} sessions this week`;
      label = 'WORKOUT'; color = C.sage; unit = 'sessions';
    }
    if (data.length && (data.some(v => v > 0) || t === 'workout')) {
      out.push({ topic: t, label, color, data, goal, current, unit, trend });
    }
  }
  return out;
}

// ── ICONS ─────────────────────────────────────────────────────────────────────

const Ic = {
  send: (c) => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M17.5 10L3.5 3.5L6.5 10L3.5 16.5L17.5 10Z" stroke={c} strokeWidth="1.5" strokeLinejoin="round"/></svg>),
  history: (c) => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7" stroke={c} strokeWidth="1.4"/><path d="M10 6v4l2.5 2" stroke={c} strokeWidth="1.4" strokeLinecap="round"/></svg>),
  user: (c) => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="7" r="3.5" stroke={c} strokeWidth="1.4"/><path d="M4 17c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke={c} strokeWidth="1.4" strokeLinecap="round"/></svg>),
  settings: (c) => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="3" stroke={c} strokeWidth="1.4"/><path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.2 4.2l1.4 1.4M14.4 14.4l1.4 1.4M4.2 15.8l1.4-1.4M14.4 5.6l1.4-1.4" stroke={c} strokeWidth="1.4" strokeLinecap="round"/></svg>),
  plus: (c) => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M10 4v12M4 10h12" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>),
  back: (c) => (<svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M12 4l-6 6 6 6" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  chevR: (c) => (<svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M8 5l5 5-5 5" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  check: (c) => (<svg width="13" height="13" viewBox="0 0 20 20" fill="none"><path d="M4 10l5 5 7-8" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  spark: (c) => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M10 2.5C10.6 5.2 11.6 7.4 13.5 8.5C11.6 9.6 10.6 11.8 10 14.5C9.4 11.8 8.4 9.6 6.5 8.5C8.4 7.4 9.4 5.2 10 2.5Z" stroke={c} strokeWidth="1.3" strokeLinejoin="round"/><circle cx="10" cy="16.5" r="1" fill={c}/></svg>),
  mic: (c) => (<svg width="15" height="15" viewBox="0 0 20 20" fill="none"><rect x="7.5" y="3" width="5" height="9.5" rx="2.5" stroke={c} strokeWidth="1.5"/><path d="M4.5 9.5C4.5 12.5 6.5 14.5 10 14.5C13.5 14.5 15.5 12.5 15.5 9.5M10 14.5V17.5M6.5 17.5H13.5" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>),
};

// ── SUGGESTIONS ───────────────────────────────────────────────────────────────

const SUGGESTIONS = [
  "Am I on track today?",
  "How is my weight trending?",
  "Am I hitting my protein goals?",
  "How's my workout consistency?",
  "Is my sleep affecting my progress?",
  "Wind-down stretches for sleep",
  "Should I fast tomorrow?",
];

// ── Dynamic ASK chips ─────────────────────────────────────────────────────
// The chips on the Coach home adapt to what the user actually asks. Every
// (non-logging) question is counted; the ones asked most often float to the
// front, and we backfill with the static SUGGESTIONS so there's always a full
// set. So the more you ask "how's my sleep", the higher it rises.
const ASK_FREQ_KEY = "hayahCoachAskFreq";
function _askNorm(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}
function recordCoachAsk(text) {
  const t = String(text || "").trim();
  if (t.length < 4 || t.length > 70) return;            // skip empties + long pastes
  if (/^\s*(log|add|record|track|note)\b/i.test(t)) return; // logging, not an enquiry
  const key = _askNorm(t);
  if (!key) return;
  try {
    const m = JSON.parse(localStorage.getItem(ASK_FREQ_KEY) || "{}") || {};
    const prev = m[key] || { count: 0 };
    m[key] = { text: t, count: prev.count + 1, lastAt: Date.now() };
    // Keep the map bounded — top 40 by count then recency.
    const top = Object.entries(m)
      .sort((a, b) => (b[1].count - a[1].count) || (b[1].lastAt - a[1].lastAt))
      .slice(0, 40);
    localStorage.setItem(ASK_FREQ_KEY, JSON.stringify(Object.fromEntries(top)));
  } catch {}
}
function getDynamicSuggestions(max = 6) {
  let freq = {};
  try { freq = JSON.parse(localStorage.getItem(ASK_FREQ_KEY) || "{}") || {}; } catch {}
  const mine = Object.values(freq)
    .filter(e => e && e.text && e.count >= 2)            // 2+ asks = a real pattern
    .sort((a, b) => (b.count - a.count) || (b.lastAt - a.lastAt))
    .map(e => e.text);
  const out = [];
  const seen = new Set();
  const push = (q) => { const k = _askNorm(q); if (!k || seen.has(k)) return; seen.add(k); out.push(q); };
  mine.slice(0, 4).forEach(push);   // your most-asked first
  SUGGESTIONS.forEach(push);        // backfill with defaults
  return out.slice(0, max);
}

// Context-aware ASK chips — the right prompt for the time of day + live state.
function contextSuggestions(quick) {
  if (!quick) return [];
  const h = new Date().getHours();
  const { calRemaining, protRemaining, lastWorkoutDaysAgo, recovery, recoveryConfident } = quick;
  const out = [];
  if (h < 11) out.push("Plan my meals today");
  if (calRemaining > 0 && calRemaining <= 300) out.push("What can I still eat?");
  else if (calRemaining === 0) out.push("I'm at my calorie limit — what now?");
  if (h >= 16 && protRemaining >= 40) out.push("How do I hit my protein today?");
  if (lastWorkoutDaysAgo === 0) out.push("Did I train enough today?");
  else if ((lastWorkoutDaysAgo == null || lastWorkoutDaysAgo >= 1) && (!recoveryConfident || recovery >= 6)) out.push("What should I train today?");
  if (recoveryConfident && recovery <= 4) out.push("Should I train today?");
  if (h >= 20) out.push("Wind-down stretches for sleep");
  return out;
}

const CAPABILITIES = [
  { t:'Calorie & macro analysis',     d:'Daily and weekly trends vs your goals' },
  { t:'Weight & body comp coaching',   d:'Fat loss pacing, lean mass protection' },
  { t:'Sleep impact interpretation',   d:'How sleep affects hunger & recovery' },
  { t:'Protein strategy',              d:'Timing, targets, practical fixes' },
  { t:'Cut & bulk planning',           d:'Deficit sizing, progression adjustments' },
  { t:'Pre/post workout nutrition',    d:'Meal timing around your training' },
  { t:'Fasting coaching',              d:'Sunnah and structured fasting windows' },
];

const ACCESS_GROUPS = [
  { title:'Body', items:[
    { k:'weight', l:'Body weight',      d:'Daily weigh-ins' },
    { k:'bf',     l:'Body composition', d:'Body fat & lean mass' },
  ]},
  { title:'Nutrition', items:[
    { k:'calories', l:'Calories & macros', d:'Food logs and totals' },
    { k:'fasting',  l:'Fasting windows',   d:'Start & end times' },
  ]},
  { title:'Activity & recovery', items:[
    { k:'sleep',   l:'Sleep',    d:'Duration & quality' },
    { k:'steps',   l:'Steps',    d:'Daily movement' },
    { k:'workout', l:'Workouts', d:'Exercise sessions' },
  ]},
];

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

// `showToast` is optional — the action-button failure path below calls it
// guarded (`showToast && showToast(...)`). Declaring it as a prop is what makes
// that guard legal; without it the reference threw and the toast never showed.
export default function HayahAICoach({ darkMode, onBuildWorkout, onOpenLog, showToast }) {
  const C = darkMode ? {
    bg0:'#090909', bg1:'#0E0E10', bg2:'#131316', bg3:'#181820', bg4:'#1F1F2A',
    line:'#252530', lineS:'#1C1C26',
    // Text tiers brightened for low-brightness bedtime readability.
    // ink3 was #7A766B (~48% lum) → #A09C8E (~62%); ink4 was #5A554B
    // (~35%) → #7A766B (~48%) — date stamps, captions, monospace
    // labels visible at minimum screen brightness without losing the
    // contrast gap to primary text.
    ink1:'#F4EFE0', ink2:'#C6BFAE', ink3:'#A09C8E', ink4:'#7A766B',
    amber:'#E0A030', amberS:'#F0C26B', amberL:'#F2BC48', amberD:'rgba(224,160,48,0.13)',
    sage:'#7CC4A0', coral:'#C24A3A', teal:'#4CD9C2',
  } : {
    bg0:'#f5f1ea', bg1:'#ffffff', bg2:'#efe9dd', bg3:'#e6dfcf', bg4:'#d8cfbb',
    line:'rgba(0,0,0,0.10)', lineS:'rgba(0,0,0,0.05)',
    ink1:'#0a141c', ink2:'#1f2e3a', ink3:'#4a6070', ink4:'#8a9aa8',
    amber:'#b87008', amberS:'#cc8e00', amberL:'#cc8e00', amberD:'rgba(184,112,8,0.13)',
    sage:'#5a9576', coral:'#a83a2a', teal:'#2A9D8F',
  };
  const fontSans = "'Inter',-apple-system,system-ui,sans-serif";
  const fontSerif = "'Instrument Serif','Georgia',serif";
  const fontMono = "'JetBrains Mono','SF Mono',Menlo,monospace";

  // Restore the user's last conversation on mount — without this, AI
  // Coach re-opened to a blank chat even when the user just had a
  // conversation going. Also useful for the /verify simulator harness
  // which seeds hayahCoachCurrentId + hayahCoachHistory and expects the
  // chat to land already populated.
  const __initialConversations = (() => { try { return loadConversations(); } catch { return []; } })();
  const __initialCurrentId = (() => { try { return localStorage.getItem("hayahCoachCurrentId") || null; } catch { return null; } })();
  const __initialMessages = (() => {
    if (!__initialCurrentId) return [];
    const c = __initialConversations.find(x => x && x.id === __initialCurrentId);
    return (c && Array.isArray(c.messages)) ? c.messages : [];
  })();
  const [messages, setMessages] = useState(__initialMessages);
  const [currentId, setCurrentId] = useState(__initialCurrentId);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("main");
  const [conversations, setConversations] = useState(__initialConversations);
  const [access, setAccess] = useState(() => loadAccess());

  // Keyboard-aware composer: track the visual viewport so the fixed composer
  // can sit flush above the tab bar (no keyboard) or flush above the keyboard
  // (when open). Same baseline-tracking pattern as TabBar.js — under Capacitor
  // KeyboardResize.Native, both window.innerHeight and vv.height shrink with
  // the keyboard, so a static baseline-on-mount alone wouldn't detect anything.
  const [kbOpen, setKbOpen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    let baseline = vv.height;
    const onResize = () => {
      if (vv.height > baseline) baseline = vv.height;
      setKbOpen(baseline - vv.height > 150);
    };
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);

  // Dictation — uses Capacitor SpeechRecognition on native (TestFlight),
  // webkitSpeechRecognition fallback in the PWA. See src/dictate.js.
  const [dictating, setDictating] = useState(false);
  const [dictError, setDictError] = useState(null);
  const dictSessionRef = useRef(null);
  const dictBaseRef = useRef("");
  // Read coach prefs for voice-mode behavior. Falls back to sensible defaults
  // if prefs schema hasn't migrated yet (older installs).
  const { prefs: hayahPrefs, update: updateHayahPrefs } = useHayahPrefs();

  // ── AI Settings / Usage tab state ──────────────────────────────────────
  const [geminiKey, setGeminiKey] = useState(getGeminiKey);
  const [geminiModel, setGeminiModel] = useState(getGeminiModel);
  const [testState, setTestState] = useState(null); // null | "loading" | "ok" | "error"
  const [testMsg, setTestMsg] = useState("");
  // About Me fields (mirror Settings panel; written to same localStorage keys)
  const [bioText, setBioText] = useState(() => { try { return localStorage.getItem("hayahUserBio") || ""; } catch { return ""; } });
  const [regionText, setRegionText] = useState(() => { try { return localStorage.getItem("hayahUserRegion") || "South Africa"; } catch { return "South Africa"; } });
  const [faithCtx, setFaithCtx] = useState(() => { try { return localStorage.getItem("hayahFaithContext") !== "false"; } catch { return true; } });
  // Token usage state — refreshed on focus + storage events.
  const [usageState, setUsageState] = useState(() => {
    try { return { summary: getUsageSummary(), last7: getLast7Days() }; } catch { return { summary: null, last7: [] }; }
  });
  // Actual Gemini bill (as reported by Google Cloud Billing / AI Studio).
  // Capturing this does two things:
  //   1. Shows the user how far their local estimate drifts from the real bill.
  //   2. Derives a calibration factor (actual / estimate-at-recording) that
  //      gets multiplied into every displayed cost going forward, so the
  //      Total Spend / By Feature / Last 7 Days numbers self-correct toward
  //      the truth. The factor is clamped to a sane band (0.5x–2x) so a
  //      bad input can't make the displayed numbers absurd.
  const [actualSpend, setActualSpend] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem("hayahActualSpendUSD") || "");
      return Number.isFinite(v) ? v : null;
    } catch { return null; }
  });
  const [actualSpendAt, setActualSpendAt] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("hayahActualSpendAt") || "", 10);
      return Number.isFinite(v) ? v : null;
    } catch { return null; }
  });
  // The raw (uncalibrated) estimate at the moment the user recorded actual.
  // This is the calibration anchor — if we ever re-applied calibration to
  // the *calibrated* current estimate we'd compound the correction.
  const [estimateAtRecording, setEstimateAtRecording] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem("hayahEstimateAtRecordingUSD") || "");
      return Number.isFinite(v) ? v : null;
    } catch { return null; }
  });
  // Modal state for the actual-spend prompt (replaces window.prompt so we
  // can drive the keyboard to numeric/decimal and style consistently).
  const [actualPromptOpen, setActualPromptOpen] = useState(false);
  const [actualPromptValue, setActualPromptValue] = useState("");
  const saveActualSpend = (val, currentRawEstimate) => {
    const n = parseFloat(String(val).replace(/,/g, ".").replace(/[^0-9.\-]/g, ""));
    if (!Number.isFinite(n) || n < 0) return;
    const ts = Date.now();
    const anchor = Number(currentRawEstimate || 0);
    try {
      localStorage.setItem("hayahActualSpendUSD", String(n));
      localStorage.setItem("hayahActualSpendAt", String(ts));
      localStorage.setItem("hayahEstimateAtRecordingUSD", String(anchor));
    } catch {}
    setActualSpend(n);
    setActualSpendAt(ts);
    setEstimateAtRecording(anchor);
  };
  const clearActualSpend = () => {
    try {
      localStorage.removeItem("hayahActualSpendUSD");
      localStorage.removeItem("hayahActualSpendAt");
      localStorage.removeItem("hayahEstimateAtRecordingUSD");
    } catch {}
    setActualSpend(null);
    setActualSpendAt(null);
    setEstimateAtRecording(null);
  };
  // Calibration factor: clamped multiplier we apply to every cost so the
  // displayed total drifts toward the real bill. Null when we have no
  // anchor (raw estimate is shown as-is).
  const calibrationFactor = useMemo(() => {
    if (actualSpend == null || !Number.isFinite(estimateAtRecording) || estimateAtRecording <= 0) return null;
    const raw = actualSpend / estimateAtRecording;
    if (!Number.isFinite(raw) || raw <= 0) return null;
    return Math.max(0.5, Math.min(2.0, raw));
  }, [actualSpend, estimateAtRecording]);
  const applyCal = (raw) => calibrationFactor == null ? Number(raw || 0) : Number(raw || 0) * calibrationFactor;
  // Seed the historical baseline once. If user has already used v2 the
  // seed is a no-op (gated by the localStorage flag in aiTokenLog).
  useEffect(() => {
    try {
      if (seedBaselineIfNeeded()) {
        setUsageState({ summary: getUsageSummary(), last7: getLast7Days() });
      }
    } catch {}
    const refresh = () => {
      try { setUsageState({ summary: getUsageSummary(), last7: getLast7Days() }); } catch {}
    };
    const onStorage = e => { if (!e.key || e.key === "hayahTokenUsage") refresh(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", refresh);
    };
  }, []);
  const voiceMode = hayahPrefs?.coach?.voiceMode || "auto-send";
  const autoSendCancelMs = Number.isFinite(hayahPrefs?.coach?.autoSendCancelMs)
    ? hayahPrefs.coach.autoSendCancelMs
    : 1500;
  // pendingAutoSend: { text, expiresAt, timerId } | null
  // Set during the post-dictation grace window. Renders a tap-to-cancel chip
  // and fires send() once expiresAt is reached unless the user taps it.
  const [pendingAutoSend, setPendingAutoSend] = useState(null);
  // Live mic level (0…1) — drives the 3-bar oscillator next to the mic.
  const [micLevel, setMicLevel] = useState(0);
  // Full-screen Voice Mode overlay — entry point is the phone-icon button
  // next to the mic. Owns its own dictation session + TTS playback.
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  // Tick state to redraw the countdown roughly every 100ms.
  const [, setCountdownTick] = useState(0);
  useEffect(() => {
    if (!pendingAutoSend) return;
    const id = setInterval(() => setCountdownTick(t => t + 1), 100);
    return () => clearInterval(id);
  }, [pendingAutoSend]);
  // Cancel any in-flight auto-send countdown — used when the user taps the
  // cancel chip, hits send manually, or starts a new dictation.
  const cancelPendingAutoSend = () => {
    if (!pendingAutoSend) return false;
    try { clearTimeout(pendingAutoSend.timerId); } catch {}
    setPendingAutoSend(null);
    return true;
  };

  const toggleDictation = async () => {
    // Light haptic on mic press so the tap feels tactile (matches iOS keyboard
    // dictation key).
    try { hapticTap(); } catch {}
    if (dictating) {
      await dictSessionRef.current?.stop?.();
      setDictating(false);
      setMicLevel(0);
      return;
    }
    // Tapping the mic during a pending auto-send means the user wants to
    // continue dictating — cancel the countdown so we don't fire mid-take.
    cancelPendingAutoSend();
    setDictError(null);
    dictBaseRef.current = input;
    const session = await startDictation({
      onPartial: (live) => {
        setInput(dictBaseRef.current ? (dictBaseRef.current + " " + live).trim() : live);
      },
      onFinal: (final) => {
        const next = dictBaseRef.current ? (dictBaseRef.current + " " + final).trim() : final;
        setInput(next);
        dictBaseRef.current = next;
      },
      onAudioLevel: (lvl) => setMicLevel(lvl),
      onError: (msg) => { setDictError(sanitizeError(msg)); setDictating(false); setMicLevel(0); },
      onEnd: (reason) => {
        setDictating(false);
        if (reason !== "speech") return;
        const finalText = dictBaseRef.current?.trim() || "";
        if (!finalText) return;
        dictBaseRef.current = "";
        // Respect voice-mode pref: review leaves text in input for editing.
        if (voiceMode === "review") return;
        // No grace window — send immediately.
        if (autoSendCancelMs <= 0) {
          setTimeout(() => send(finalText), 0);
          return;
        }
        // Tap-to-cancel grace window. Timer fires send() unless the user
        // dismisses the chip first.
        const timerId = setTimeout(() => {
          setPendingAutoSend(null);
          send(finalText);
        }, autoSendCancelMs);
        setPendingAutoSend({ text: finalText, expiresAt: Date.now() + autoSendCancelMs, timerId });
        try { hapticTap(); } catch {}
      },
    });
    dictSessionRef.current = session;
    setDictating(true);
  };

  // Word-by-word streaming for AI replies — matches the v2 Chat View design.
  // While `streaming` is true, the message list renders a virtual AI bubble
  // whose body is `streamText` (revealed at ~52ms/word) plus an amber blinking
  // cursor. When streaming completes the message is committed to `messages`
  // with its parsed follow-up suggestions.
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const streamTimerRef = useRef(null);
  // Stop/abort plumbing — lets the user halt a response mid-flight (like the
  // stop button in Claude/ChatGPT). stopRef short-circuits in-flight awaits;
  // streamTextRef mirrors the visible text so a stop can finalise the partial;
  // activeSendRef holds the in-flight {history,id} so the partial can be saved.
  const stopRef = useRef(false);
  const streamTextRef = useRef("");
  const activeSendRef = useRef(null);
  // Track which AI message has been copied so the Copy button can flip to
  // "Copied" (sage) for ~1.6s before reverting.
  const [copiedIdx, setCopiedIdx] = useState(null);
  useEffect(() => () => { if (streamTimerRef.current) clearInterval(streamTimerRef.current); }, []);

  // Stop the current response. Halts the typewriter, drops any in-flight
  // result (via stopRef checks after awaits), and keeps whatever text was
  // already shown as a finalised message.
  const stopGeneration = () => {
    stopRef.current = true;
    if (streamTimerRef.current) { clearInterval(streamTimerRef.current); streamTimerRef.current = null; }
    const partial = (streamTextRef.current || "").trim();
    const ctx = activeSendRef.current;
    setStreaming(false);
    setLoading(false);
    setStreamText("");
    streamTextRef.current = "";
    try { hapticTap(); } catch {}
    if (partial && ctx) {
      const aiMsg = { role: "ai", text: partial, followUps: [], stopped: true };
      const next = [...ctx.history, aiMsg];
      setMessages(next);
      try { upsertConversation(ctx.id, next); } catch {}
    }
    activeSendRef.current = null;
  };

  // Photo food logging — { dataUrl } until the user sends.
  const [pendingImage, setPendingImage] = useState(null);
  const photoInputRef = useRef(null);
  // Message-count at which the user last summarised. The "Summarise this
  // session" pill is gated on (messages.length >= summarisedAt + 3) so it
  // doesn't keep nagging after a summary lands.
  const [summarisedAt, setSummarisedAt] = useState(-1);
  // Transient banner — surfaces summary failures without writing them
  // into the conversation history as an AI message.
  const [summaryError, setSummaryError] = useState(null);
  const summaryErrorTimerRef = useRef(null);
  useEffect(() => () => { if (summaryErrorTimerRef.current) clearTimeout(summaryErrorTimerRef.current); }, []);

  // ── PHOTO PICKER ───────────────────────────────────────────────────────────
  async function pickMealPhoto() {
    if (loading || streaming) return;
    if (Capacitor.isNativePlatform()) {
      try {
        const photo = await CapacitorCamera.getPhoto({ quality: 70, allowEditing: false, resultType: 'dataUrl', source: 'PROMPT' });
        const src = photo?.dataUrl || photo?.webPath || photo?.path;
        if (!src) return;
        let dataUrl = src;
        if (!dataUrl.startsWith('data:')) {
          try {
            const blob = await fetch(src).then(r => r.blob());
            dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
          } catch { return; }
        }
        setPendingImage({ dataUrl });
        return;
      } catch (e) {
        if (/cancel/i.test(e?.message || String(e || ''))) return;
        // plugin error — fall through to file input
      }
    }
    photoInputRef.current?.click();
  }
  function onPhotoInputChange(e) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPendingImage({ dataUrl: reader.result });
    reader.readAsDataURL(f);
  }

  // ── SESSION SUMMARY ────────────────────────────────────────────────────────
  async function summariseSession() {
    if (loading || streaming) return;
    const summaryPrompt = "Summarise this conversation as a SESSION SUMMARY. Output exactly 3 sections separated by blank lines, NO markdown headers: (1) Two short sentences on what we discussed. (2) Three single-line key takeaways, each starting with '• '. (3) One concrete action for tomorrow, starting with 'Tomorrow: '. Do NOT include a <followups> tag.";
    setLoading(true);
    const id = currentId || newId();
    if (!currentId) setCurrentId(id);
    try {
      // v2: stable persona/profile system block + summary directive.
      // The data context goes into the user-side via buildCoachContext
      // appended to the last message so the persona block stays
      // identical across this conversation's turns.
      const allData = readAllData();
      const ctxBlock = buildCoachContext("session summary across the conversation", allData, access);
      try {
        localStorage.setItem("sirat-debug-coach-ctx", ctxBlock);
        localStorage.setItem("sirat-debug-coach-ctx-at", String(Date.now()));
        localStorage.setItem("sirat-debug-coach-ctx-query", "session summary across the conversation");
      } catch {}
      const augmented = [...messages];
      if (augmented.length > 0) {
        const last = augmented[augmented.length - 1];
        augmented[augmented.length - 1] = { ...last, text: `Context:\n${ctxBlock}\n\n${last.text || ""}` };
      }
      const sys = composeSystem([buildSystemInstruction(), summaryPrompt]);
      let summaryMeta = null;
      const reply = await callGeminiChat(augmented, {
        systemInstruction: sys,
        // 4096 — coachSummary uses gemini-2.5-pro; thinking can chew
        // through 1024 before the 3-section session-summary output
        // starts.
        model: MODELS.coachSummary,
        maxOutputTokens: 4096,
        feature: "coachSummary",
        onUsage: (m) => { summaryMeta = m; },
      });
      const aiMsg = { role: 'ai', text: (reply || '').trim(), kind: 'summary' };
      if (summaryMeta && summaryMeta.usage) {
        const u = summaryMeta.usage;
        const inT  = u.promptTokenCount     ?? u.promptTokens     ?? 0;
        const outT = u.candidatesTokenCount ?? u.completionTokens ?? 0;
        aiMsg.cost = { usd: costUSD(summaryMeta.modelId, inT, outT), inT, outT, modelId: summaryMeta.modelId };
      }
      const next = [...messages, aiMsg];
      setMessages(next);
      upsertConversation(id, next);
      setSummarisedAt(messages.length);
    } catch (err) {
      // Don't pollute the conversation with the error — surface it as a
      // transient toast-style banner instead, and leave the chat history
      // clean so the user can retry without seeing "Couldn't summarise"
      // forever. The button at the bottom remains tappable.
      try { hapticError(); } catch {}
      setSummaryError(err && err.message ? err.message : "Summary failed — try again");
      // Clear the banner after 6s so it doesn't linger.
      try {
        if (summaryErrorTimerRef.current) clearTimeout(summaryErrorTimerRef.current);
        summaryErrorTimerRef.current = setTimeout(() => setSummaryError(null), 6000);
      } catch {}
    } finally {
      setLoading(false);
    }
  }
  // History search query — filters conversations by topic / preview.
  const [historyQuery, setHistoryQuery] = useState("");

  const endRef = useRef(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  // Near-bottom tracking so auto-scroll doesn't yank a user reading history.
  const [atBottom, setAtBottom] = useState(true);
  const atBottomRef = useRef(true);
  const onChatScroll = () => {
    const el = scrollRef.current; if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    atBottomRef.current = near; setAtBottom(near);
  };
  const scrollToLatest = () => {
    atBottomRef.current = true; setAtBottom(true);
    try { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); } catch {}
  };
  const [feedback, setFeedback] = useState(loadFeedback);
  const rateMessage = (msgId, val) => {
    if (!msgId) return;
    try { hapticHayahSelection(); } catch {}
    setFeedback(prev => { const next = { ...prev }; if (next[msgId] === val) delete next[msgId]; else next[msgId] = val; saveFeedback(next); return next; });
  };
  // Run a tappable coach suggestion (logFood/logWater/quickAdd): execute, mark
  // the button done, and append the same applied-action chip the auto-log uses.
  const runActionButton = (mi, bi) => {
    const msg = messages[mi];
    if (!msg || !Array.isArray(msg.actionButtons)) return;
    const b = msg.actionButtons[bi];
    if (!b || b._done) return;
    let applied = null;
    try {
      if (b.kind === "logFood") {
        // If the model omitted items (only provided label/slot), synthesise a
        // basic entry from the label — better than silently doing nothing.
        const items = Array.isArray(b.items) && b.items.length
          ? b.items
          : [{ name: b.label.replace(/^log\s+/i, "").trim() || b.label, calories: 0, protein: 0, carbs: 0, fat: 0, servingLabel: "1 serving" }];
        const slot = b.slot || "meal";
        const r = logFood(slot, items);
        if (r.count) applied = { label: `🍽️ Logged ${items.map(x => x.name).join(", ")}${r.kcal ? ` · +${r.kcal} kcal` : ""}`, undo: { slot: r.slot, ids: r.ids } };
      } else if (b.kind === "logWater") {
        const r = logWater({ ml: b.ml });
        if (r.cups) applied = { label: `💧 Water logged · ${r.cups} ${r.cups === 1 ? "cup" : "cups"} today` };
      } else if (b.kind === "quickAdd") {
        addFavoriteFood((b.items && b.items[0]) || { name: b.label });
        applied = { label: "⚡ Added to Quick Add" };
      }
    } catch {}
    // Only mark done + haptic if logging actually worked.
    if (!applied) {
      try { showToast && showToast("Couldn't log — try asking the coach to log it directly."); } catch {}
      return;
    }
    try { hapticHayahSuccess(); } catch {}
    let nextMsgs = null;
    setMessages(prev => {
      nextMsgs = prev.map((m, i) => {
        if (i !== mi) return m;
        const actionButtons = m.actionButtons.map((x, k) => k === bi ? { ...x, _done: true } : x);
        const appliedActions = [...(m.appliedActions || []), applied];
        return { ...m, actionButtons, appliedActions };
      });
      return nextMsgs;
    });
    if (currentId && nextMsgs) { try { upsertConversation(currentId, nextMsgs); } catch {} }
  };
  // Edit & resend a user message: drop it + everything after, load text back.
  const editUserMessage = (idx) => {
    if (loading || streaming) return;
    const msg = messages[idx];
    if (!msg || msg.role !== "user") return;
    try { hapticTap(); } catch {}
    const truncated = messages.slice(0, idx);
    setMessages(truncated);
    if (currentId) { try { upsertConversation(currentId, truncated); } catch {} }
    setInput(msg.text || "");
    atBottomRef.current = true; setAtBottom(true);
    setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 60);
  };
  const hasKey = !!getGeminiKey();
  const stats = useMemo(() => todayStats(), [messages.length === 0, view]);
  const insights = useMemo(() => computeInsights(stats, access, C), [stats, access, darkMode]);
  const quick = useMemo(() => hayahQuickStats(), [messages.length === 0, view, stats]);

  const askWhatToEat = () => {
    const q = quick;
    const pieces = [];
    pieces.push(`What should I eat now? It's around ${q.timeOfDay} time.`);
    pieces.push(`I have roughly ${q.calRemaining} kcal, ${q.protRemaining}g protein, ${q.carbsRemaining}g carbs and ${q.fatRemaining}g fat left for today.`);
    pieces.push(`Suggest 2-3 specific, realistic meal options that fit those macros — name the foods and portions, give an estimated macro breakdown for each option, and prefer halal South-African-friendly choices. Mention which option best fills the biggest gap (likely protein) and end with a one-line pick.`);
    send(pieces.join(" "));
  };

  const askWhatToTrain = () => {
    const q = quick;
    const pieces = [];
    pieces.push(`What should I train today?`);
    if (q.lastWorkoutDaysAgo === 0) pieces.push("I already trained today.");
    else if (q.lastWorkoutDaysAgo != null) pieces.push(`Last trained ${q.lastWorkoutDaysAgo === 1 ? "yesterday" : `${q.lastWorkoutDaysAgo} days ago`}${q.lastWorkoutName ? ` (${q.lastWorkoutName})` : ""}.`);
    if (q.recoveryConfident) pieces.push(`Self-rated recovery from sleep/HRV/RHR is roughly ${q.recovery}/10.`);
    pieces.push(`Look at my last 14 days of Riyadah sessions to balance the split — pick the most-deserved muscle group(s), or call a rest day if recovery is poor. If training: give 4-6 exercises with sets and rep ranges suited to my goal.`);
    send(pieces.join(" "));
  };

  const [menuOpen, setMenuOpen]             = useState(false);
  const [menuRestaurant, setMenuRestaurant] = useState("");
  const [menuText, setMenuText]             = useState("");

  const submitMenu = () => {
    const q = quick;
    const restaurant = menuRestaurant.trim();
    const menu = menuText.trim();
    if (!menu || loading) return;
    const pieces = [];
    pieces.push(`I am at a restaurant${restaurant ? ` (${restaurant})` : ""}. Time is around ${q.timeOfDay}. I have roughly ${q.calRemaining} kcal, ${q.protRemaining}g protein, ${q.carbsRemaining}g carbs and ${q.fatRemaining}g fat left for the rest of today.`);
    pieces.push(`Here is the menu I'm looking at:\n${menu}`);
    pieces.push(`Rank the top 3 items I should order. For each: name, estimated kcal/protein/carbs/fat, why it fits (or doesn't), and any modification I should ask for (no rice, swap chips for salad, sauce on the side, etc.). If everything is bad for my macros, say so plainly and give the least-damaging pick. Prefer halal options. Finish with a single line: "My pick: ...".`);
    send(pieces.join("\n\n"));
    setMenuOpen(false);
    setMenuText("");
    setMenuRestaurant("");
  };


  // Scroll to bottom only when there are actual messages or loading
  useEffect(() => {
    if (messages.length === 0 && !loading && !streamText) return;
    if (view !== "main") return;
    if (!endRef.current) return;
    if (!atBottomRef.current) return; // user scrolled up to read — don't yank them
    // While streaming, use "auto" behaviour — smooth scroll fights the
    // rapid chunk updates and falls behind. On commit / view change we
    // still use smooth so the final scroll feels deliberate.
    const behavior = streaming ? "auto" : "smooth";
    const t = setTimeout(() => endRef.current?.scrollIntoView({ behavior, block: "end" }), 30);
    return () => clearTimeout(t);
  }, [messages, loading, view, streamText, streaming]);

  // Snap scroll to top whenever the view changes
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [view]);

  // Sync textarea height to content on every input change so dictation
  // (which calls setInput programmatically) grows the box the same way
  // typing does. The onChange-only resize missed that case, leaving the
  // user unable to see long dictated text.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (input === "") { el.style.height = ""; return; }
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  }, [input]);

  // Persist conversations whenever they change
  useEffect(() => { saveConversations(conversations); }, [conversations]);
  // Persist currentId so the next launch resumes the same conversation.
  useEffect(() => {
    try {
      if (currentId) localStorage.setItem("hayahCoachCurrentId", currentId);
      else           localStorage.removeItem("hayahCoachCurrentId");
    } catch {}
  }, [currentId]);
  useEffect(() => { saveAccess(access); }, [access]);

  function upsertConversation(id, msgs) {
    setConversations(prev => {
      const existingIdx = prev.findIndex(c => c.id === id);
      const now = Date.now();
      if (existingIdx >= 0) {
        const copy = [...prev];
        copy[existingIdx] = { ...copy[existingIdx], messages: msgs, lastUpdatedAt: now, topic: inferTopic(msgs) };
        // Move to top
        const [item] = copy.splice(existingIdx, 1);
        return [item, ...copy];
      }
      return [{ id, startedAt: now, lastUpdatedAt: now, topic: inferTopic(msgs), messages: msgs }, ...prev];
    });
  }

  function startNew() {
    try { hapticTap(); } catch {}
    setMessages([]);
    setCurrentId(null);
    setInput("");
    setView("main");
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function resumeConversation(c) {
    try { hapticTap(); } catch {}
    setMessages(c.messages || []);
    setCurrentId(c.id);
    setView("main");
  }

  function deleteConversation(id) {
    try { hapticConfirm(); } catch {}
    setConversations(prev => prev.filter(c => c.id !== id));
    if (currentId === id) { setMessages([]); setCurrentId(null); }
  }

  async function send(text) {
    try { hapticConfirm(); } catch {}
    const hasImage = !!pendingImage?.dataUrl;
    if ((!text?.trim() && !hasImage) || loading || streaming) return;
    const userMsg = {
      role: "user",
      text: (text || '').trim(),
      ...(hasImage ? { image: pendingImage.dataUrl } : {}),
    };
    // Learn what the user asks so the home ASK chips can surface their
    // most-frequent enquiries (skips logging commands inside recordCoachAsk).
    try { recordCoachAsk(userMsg.text); } catch {}
    const history = [...messages, userMsg];
    const id = currentId || newId();
    if (!currentId) setCurrentId(id);
    // Arm the stop button: clear any prior stop flag and record this send's
    // base history so stopGeneration() can finalise a partial response.
    stopRef.current = false;
    activeSendRef.current = { history, id };
    atBottomRef.current = true; setAtBottom(true); // sending always pins to bottom
    setMessages(history);
    upsertConversation(id, history);
    // Stop any in-flight dictation session and clear its base, otherwise
    // a late partial/final from the still-alive recognizer reappends the
    // old text right after we clear the input. Also kill any pending
    // auto-send countdown — the user is sending now.
    if (dictSessionRef.current) {
      try { dictSessionRef.current.stop?.(); } catch {}
      dictSessionRef.current = null;
    }
    dictBaseRef.current = "";
    setDictating(false);
    if (pendingAutoSend) {
      try { clearTimeout(pendingAutoSend.timerId); } catch {}
      setPendingAutoSend(null);
    }
    setInput("");
    if (hasImage) setPendingImage(null);
    setLoading(true);
    setStreaming(false);
    setStreamText("");
    try {
      // v2: stable persona/profile (Gemini implicit-caching friendly).
      const sys = buildSystemInstruction();
      // Per-turn relevance-classified data context, prepended to the
      // user's message so the persona block stays bit-identical across turns.
      const allData = readAllData();
      const queryForRelevance = (text && text.trim()) || (hasImage ? "photo of meal" : "");
      const ctxBlock = buildCoachContext(queryForRelevance, allData, access);
      // Debug breadcrumb — write the latest context to localStorage so the
      // user can open "Inspect AI Context" in Settings and see exactly what
      // the Coach received. Critical when the AI claims "no data" but the
      // user knows the data exists.
      try {
        localStorage.setItem("sirat-debug-coach-ctx", ctxBlock);
        localStorage.setItem("sirat-debug-coach-ctx-at", String(Date.now()));
        localStorage.setItem("sirat-debug-coach-ctx-query", queryForRelevance || "");
      } catch {}

      // Latest call's usage + model — populated by the onUsage callback
      // wired into every call below. Attached to the AI message so the
      // chat row can show a tiny per-message cost chip.
      let lastCallMeta = null;
      const captureUsage = (meta) => { lastCallMeta = meta; };

      // Commit the final reply to messages + storage. Shared by both paths.
      const commitReply = (replyText, followUps, cards, workout, actionState, actionButtons) => {
        if (stopRef.current) return; // user stopped — stopGeneration already finalised
        setStreaming(false);
        setStreamText("");
        streamTextRef.current = "";
        activeSendRef.current = null;
        try { hapticHayahSuccess(); } catch {}
        const aiMsg = { role: "ai", id: newId(), text: replyText, followUps, cards, workout: workout || null,
          actionButtons: Array.isArray(actionButtons) && actionButtons.length ? actionButtons : null,
          appliedActions: actionState?.applied || null, pendingActions: actionState?.pending || null };
        if (lastCallMeta && lastCallMeta.usage) {
          const u = lastCallMeta.usage;
          const inT  = u.promptTokenCount     ?? u.promptTokens     ?? 0;
          const outT = u.candidatesTokenCount ?? u.completionTokens ?? 0;
          const cost = costUSD(lastCallMeta.modelId, inT, outT);
          aiMsg.cost = { usd: cost, inT, outT, modelId: lastCallMeta.modelId };
        }
        const next = [...history, aiMsg];
        setMessages(next);
        upsertConversation(id, next);
      };

      // v1 fake-stream replay — 2 words per 52ms tick. Used by the text
      // path (schema-structured output can't be safely partial-rendered)
      // and as the fallback when SSE streaming throws.
      const fakeStreamReplay = (replyText, followUps, cards, workout, actionState, actionButtons) => {
        setLoading(false);
        setStreaming(true);
        setStreamText("");
        const words = String(replyText || "").split(/(\s+)/);
        let i = 0;
        const tick = () => {
          if (stopRef.current) { clearInterval(streamTimerRef.current); streamTimerRef.current = null; return; }
          i += 2;
          const visible = words.slice(0, i).join('');
          setStreamText(visible);
          streamTextRef.current = visible;
          if (i >= words.length) {
            clearInterval(streamTimerRef.current);
            streamTimerRef.current = null;
            commitReply(replyText, followUps, cards, workout, actionState, actionButtons);
          }
        };
        streamTimerRef.current = setInterval(tick, 52);
      };

      if (hasImage) {
        // Photo path — real SSE streaming, plain text. The vision call
        // doesn't produce cards/followups (consistent with v1).
        const userQuestion = (text && text.trim())
          ? text.trim()
          : "Estimate the macros in this meal (calories, protein, carbs, fat). Tell me if it fits my remaining targets. Reference my actual numbers.";
        const visionPrompt = `Context:\n${ctxBlock}\n\n${userQuestion}`;
        const imageUrl = userMsg.image;
        try {
          setLoading(false);
          setStreaming(true);
          setStreamText("");
          let accumulated = "";
          await callGeminiVisionStream(visionPrompt, imageUrl, {
            systemInstruction: sys,
            model: MODELS.coachVision,
            maxOutputTokens: 2048,
            feature: "coachVision",
            onChunk: (_chunk, acc) => { if (stopRef.current) return; accumulated = acc; setStreamText(acc); streamTextRef.current = acc; },
            onUsage: captureUsage,
          });
          if (stopRef.current) return;
          commitReply(accumulated, [], []);
        } catch (streamErr) {
          // Fall back to non-streaming + fake-stream replay. The central
          // fallback chain inside callGeminiVision handles model degrade.
          const reply = await callGeminiVision(visionPrompt, imageUrl, {
            systemInstruction: sys,
            model: MODELS.coachVision,
            maxOutputTokens: 2048,
            feature: "coachVision",
            onUsage: captureUsage,
          });
          fakeStreamReplay(reply || "", [], []);
        }
      } else if (wantsWindDown(text)) {
        // ── WIND-DOWN ROUTINE (deterministic, no model call) ────────────
        // A gentle bedtime stretch + breathing flow, surfaced through the same
        // "Open in Exercise" card pipeline as an AI-built workout.
        const tmpl = coachWorkoutToTemplate(windDownWorkout());
        const reply = "Here's a gentle wind-down you can do right in bed — move slowly, breathe deep through your nose, and never force a stretch. Tap ‘Open in Exercise’ to begin.";
        fakeStreamReplay(reply, ["How does this help me sleep?", "Can I do it lying down?", "Make it shorter"], [], tmpl, null);
        return;
      } else if (wantsToLog(text) && !wantsWorkoutBuild(text, history)) {
        // ── LOCAL-FIRST LOG PATH ────────────────────────────────────────
        // Before spending an AI call, try to resolve the food from the user's
        // OWN data — recent diary entries, Quick-Add favourites, saved foods.
        // If every item matches, log it instantly with the exact macros the
        // user already trusts (no AI cost, no estimate drift). Any miss falls
        // through to the AI extractor below.
        try {
          const local = resolveLocalLog(text);
          if (local && local.groups && Object.keys(local.groups).length) {
            const appliedLocal = [];
            for (const [slot, items] of Object.entries(local.groups)) {
              const r = logFood(slot, items);
              if (r.count) appliedLocal.push({ label: `🍽️ Logged ${items.map(i => i.name).join(", ")}${r.kcal ? ` · +${r.kcal} kcal` : ""}`, undo: { slot: r.slot, ids: r.ids } });
            }
            if (appliedLocal.length) {
              const totalK = Object.values(local.groups).flat().reduce((s, i) => s + (i.calories || 0), 0);
              const replyText = `Logged ${local.names.join(", ")}${totalK ? ` · +${Math.round(totalK)} kcal` : ""} — matched from ${local.source}, no AI needed.`;
              fakeStreamReplay(replyText, [], [], null, { applied: appliedLocal });
              return;
            }
          }
        } catch {}
        // ── DEDICATED LOG PATH (AI) ─────────────────────────────────────
        // The conversational model kept claiming "logged it" in prose without
        // emitting the structured action (schema required/minItems didn't fix
        // it). So logging gets its OWN focused extraction call whose only job
        // is to return what to log — and the reply is built from what was
        // ACTUALLY written, so it can never lie.
        const EXTRACT_SCHEMA = {
          type: "object",
          properties: {
            food: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: { type: "string" },
                  slot: { type: "string" },
                  calories: { type: "number" },
                  protein: { type: "number" },
                  carbs: { type: "number" },
                  fat: { type: "number" },
                  servingLabel: { type: "string" },
                },
                // Macros REQUIRED — the model kept omitting them, logging foods
                // at 0 kcal. Required forces a real estimate.
                required: ["name", "calories", "protein", "carbs", "fat"],
              },
            },
            waterMl: { type: "number" },
            weightKg: { type: "number" },
            mounjaro: { type: "boolean" },
            note: { type: "string" },
          },
          required: ["note"],
        };
        const extractSys = composeSystem([
          "You extract a food/water/weight/medication log from the user's message for a calorie tracker. CRITICAL: every food item MUST carry a realistic, NON-ZERO calorie + macro estimate from your nutrition knowledge for the stated (or a typical) portion — NEVER 0 for a real food, never leave them blank. Examples: medium banana = 105 kcal, 1g P, 27g C, 0g F; cappuccino = 38 kcal, 2g P, 4g C, 1g F; 2 eggs = 140/12/1/10; slice of toast = 80/3/14/1; apple = 95/0/25/0. Pick slot: coffee/tea/juice/soft drink → drinks, protein shake → whey, creatine/vitamins/pre-workout → supplements, treats → snacks, otherwise → meal.",
          "Return JSON: food[] ({name, slot, calories, protein, carbs, fat, servingLabel}), waterMl (number, convert any units to ml), weightKg (number), mounjaro (true ONLY if they took their weekly Mounjaro/GLP-1 dose), note (one short friendly confirmation line naming what you logged). If you genuinely cannot tell what to log, leave food/water/weight empty and put a brief clarifying question in note.",
        ]);
        let rawX = "", parsedX = {};
        try {
          rawX = await callGeminiChat([{ role: "user", text: `User said: "${text}"` }], {
            systemInstruction: extractSys,
            responseSchema: EXTRACT_SCHEMA,
            model: MODELS.mealParser || MODELS.coachChat,
            maxOutputTokens: 1024,
            feature: "coachLog",
            onUsage: captureUsage,
          });
          parsedX = JSON.parse(rawX);
        } catch {
          try { parsedX = JSON.parse((rawX || "").slice((rawX || "").indexOf("{"))); } catch { parsedX = {}; }
        }
        const applied = [];
        const foods = Array.isArray(parsedX.food) ? parsedX.food.filter(f => f && f.name) : [];
        if (foods.length) {
          const bySlot = {};
          for (const f of foods) (bySlot[f.slot || "meal"] = bySlot[f.slot || "meal"] || []).push(f);
          for (const [slot, items] of Object.entries(bySlot)) {
            const r = logFood(slot, items);
            if (r.count) applied.push({ label: `🍽️ Logged ${items.map(i => i.name).join(", ")}${r.kcal ? ` · +${r.kcal} kcal` : ""}`, undo: { slot: r.slot, ids: r.ids } });
          }
        }
        if (parsedX.waterMl > 0) { const r = logWater({ ml: parsedX.waterMl }); if (r.cups) applied.push({ label: `💧 Water logged · ${r.cups} ${r.cups === 1 ? "cup" : "cups"} today` }); }
        if (parsedX.weightKg > 0) { const r = logWeight(parsedX.weightKg); if (r) applied.push({ label: `⚖️ Weight logged · ${r.kg} kg` }); }
        if (parsedX.mounjaro === true) { logMounjaro(); applied.push({ label: "💉 Mounjaro dose logged" }); }
        const replyText = applied.length
          ? (parsedX.note && /log|add|record|done/i.test(parsedX.note) ? parsedX.note : `Done — ${applied.map(a => a.label.replace(/^\S+\s/, "")).join("; ")}.`)
          : (parsedX.note || "I couldn't tell what to log — what did you have?");
        fakeStreamReplay(replyText, [], [], null, { applied: applied.length ? applied : null });
        return;
      } else {
        // Text path — schema-structured output. Cards + followups arrive
        // as parsed JSON fields, no regex tag parsing required. The
        // reply field flows through fake-stream replay for typewriter UX.
        // When the user wants a workout to do, append the exercise library +
        // builder instruction to THIS turn's context so the model can fill the
        // optional `workout` field with valid ids. History is passed so a
        // terse "build it" after a training chat still triggers.
        const ctxForTurn = wantsWorkoutBuild(text, history)
          ? `${ctxBlock}\n\n${workoutBuilderContext()}`
          : ctxBlock;
        const cappedMessages = history.slice(-12);
        const augmented = cappedMessages.map((m, idx) => {
          if (idx !== cappedMessages.length - 1) return m;
          return { ...m, text: `Context:\n${ctxForTurn}\n\n${m.text || ""}` };
        });
        // On a clear log request, force the action: required-actions schema +
        // a concrete example. (Workout-build turns keep the normal schema.)
        const loggy = wantsToLog(text) && !wantsWorkoutBuild(text, history);
        const sysWithOutputRule = composeSystem(
          loggy ? [sys, COACH_CHAT_OUTPUT_RULE, LOG_DIRECTIVE] : [sys, COACH_CHAT_OUTPUT_RULE]
        );
        const rawJson = await callGeminiChat(augmented, {
          systemInstruction: sysWithOutputRule,
          responseSchema: loggy ? COACH_CHAT_SCHEMA_LOG : COACH_CHAT_SCHEMA,
          model: MODELS.coachChat,
          // 8192 — enough headroom for long answers (e.g. "table of my last
          // 20 PRs") without truncating the JSON wrapper mid-field. 4096
          // was hitting the cap on detailed exercise queries and the
          // truncated output broke JSON.parse, falling through to the
          // raw-text fallback (which dumped malformed JSON in the UI).
          maxOutputTokens: 8192,
          feature: "coachChat",
          onUsage: captureUsage,
        });
        if (stopRef.current) return; // user stopped while awaiting the model
        let parsed;
        try { parsed = JSON.parse(rawJson); }
        catch {
          // Schema should guarantee valid JSON. But truncated outputs and
          // occasional pre-amble can still trip the parser. Try harder
          // before giving up:
          //   1. Strip prose before the first `{`
          //   2. Best-effort extract `reply` and `followups` via regex on
          //      the (possibly-truncated) text — better than showing the
          //      user a wall of JSON syntax.
          let recovered = null;
          try {
            const start = (rawJson || "").indexOf("{");
            if (start >= 0) {
              recovered = JSON.parse((rawJson || "").slice(start));
            }
          } catch {}
          if (!recovered) {
            const replyMatch = (rawJson || "").match(/"reply"\s*:\s*"((?:[^"\\]|\\.)*)/);
            const followupsBlock = (rawJson || "").match(/"followups"\s*:\s*\[([\s\S]*?)(?:\]|$)/);
            const followupsArr = followupsBlock
              ? Array.from(followupsBlock[1].matchAll(/"((?:[^"\\]|\\.)*)"/g)).map(m => m[1])
              : [];
            recovered = {
              reply: replyMatch ? replyMatch[1].replace(/\\"/g, '"').replace(/\\n/g, "\n") : "",
              followups: followupsArr,
            };
          }
          parsed = recovered;
        }
        const replyText = parsed.reply || "";
        const followUps = (Array.isArray(parsed.followups) ? parsed.followups : [])
          .map(q => String(q || "").trim()).filter(Boolean).slice(0, 3);
        // Coach-built workout → map to a runnable Riyadah template (null if the
        // model returned no/invalid exercises).
        let workoutTemplate = null;
        if (parsed.workout) { try { workoutTemplate = coachWorkoutToTemplate(parsed.workout); } catch {} }
        // Coach log actions: auto-apply exact-number ones (water/weight/Mounjaro)
        // now; stage meal/food ones for a confirm card (the user eyeballs the
        // AI's macro estimate before it hits their diary).
        const actionState = applyCoachActions(Array.isArray(parsed.actions) ? parsed.actions : []);
        const actionButtons = Array.isArray(parsed.actionButtons) ? parsed.actionButtons.filter(b => b && b.kind && b.label).slice(0, 3) : null;
        // Data-graph cards removed 2026-05-24 — see schema comment above.
        fakeStreamReplay(replyText, followUps, [], workoutTemplate, actionState, actionButtons);
      }
    } catch (err) {
      try { hapticError(); } catch {}
      const next = [...history, { role:"ai", text: `Something went wrong: ${err.message}` }];
      setMessages(next);
      upsertConversation(id, next);
      setLoading(false);
      setStreaming(false);
    }
  }

  // Hand a coach-built workout to the Exercise tab: persist it as the pending
  // workout and ask the Hub to switch features. Riyadah surfaces a Start card.
  const openCoachWorkout = (template) => {
    if (!template) return;
    try { hapticHayahSuccess(); } catch {}
    saveCoachWorkout(template);
    if (onBuildWorkout) onBuildWorkout();
  };

  // Undo an auto-applied food log — removes the written entries from the diary
  // and marks the chip undone. Persists the message change.
  const undoCoachLog = (msgIndex, chipIndex) => {
    let updated = null;
    setMessages(prev => {
      const next = prev.map((m, i) => {
        if (i !== msgIndex || !Array.isArray(m.appliedActions)) return m;
        const chip = m.appliedActions[chipIndex];
        if (!chip || !chip.undo) return m;
        try { removeFoodEntries(chip.undo.slot, chip.undo.ids); } catch {}
        const appliedActions = m.appliedActions.map((c, k) =>
          k === chipIndex ? { label: "↩︎ Removed from diary", undone: true } : c);
        return { ...m, appliedActions };
      });
      updated = next;
      return next;
    });
    try { hapticTap(); } catch {}
    if (updated && currentId) { try { upsertConversation(currentId, updated); } catch {} }
  };

  // Retry: drop the most recent AI message, re-send the most recent user
  // message through the streaming pipeline.
  const retryLast = () => {
    try { hapticConfirm(); } catch {}
    if (loading || streaming) return;
    let cutoff = messages.length;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') { cutoff = i; break; }
    }
    if (cutoff >= messages.length) return;
    const lastUserText = messages[cutoff].text;
    setMessages(messages.slice(0, cutoff));
    setTimeout(() => send(lastUserText), 30);
  };

  // Copy AI message body to clipboard with a transient "Copied" affordance.
  const copyMessage = (text, idx) => {
    try { hapticHayahSelection(); } catch {}
    try { navigator.clipboard?.writeText?.(text); } catch {}
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(prev => prev === idx ? null : prev), 1600);
  };

  // Hoisted hooks — must sit above the `if (!hasKey) return (...)` below
  // so React sees the same hook order on every render (Rules of Hooks).
  const [debugCtxOpen, setDebugCtxOpen] = useState(false);
  const [debugCtxData, setDebugCtxData] = useState(null);
  const [debugCtxInlineOpen, setDebugCtxInlineOpen] = useState(false);
  const chatSwipeBack = useSwipeBack(() => setView("history"), { edgeOnly: true });

  // Personalised ASK chips — recomputes after each message so freshly-asked
  // questions climb the list. Falls back to the static defaults.
  // Hoisted above the no-key early return with the other hooks: sitting below
  // it changed hook order the moment a key was added, which React rejects.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const askSuggestions = useMemo(() => {
    const out = []; const seen = new Set();
    const push = (q) => { const k = _askNorm(q); if (!k || seen.has(k)) return; seen.add(k); out.push(q); };
    contextSuggestions(quick).slice(0, 3).forEach(push);
    getDynamicSuggestions(6).forEach(push);
    return out.slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, conversations, quick]);

  // ── NO-KEY STATE ────────────────────────────────────────────────────────────

  if (!hasKey) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",padding:"40px 24px",textAlign:"center",background:C.bg0,fontFamily:fontSans}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400;500&display=swap');@keyframes haic-orb{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}`}</style>
      <CoachOrb size={64} pulse={true} C={C}/>
      <div style={{fontFamily:fontSerif,fontStyle:'italic',fontSize:24,color:C.ink1,marginTop:20,marginBottom:6,letterSpacing:'-0.02em'}}>AI Coach</div>
      <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.16em',textTransform:'uppercase',color:C.amber,marginBottom:14}}>Personal Health Analyst</div>
      <div style={{fontSize:13,color:C.ink3,lineHeight:1.7,maxWidth:290}}>
        Add your Gemini API key in <strong style={{color:C.ink1,fontWeight:600}}>Hayah → Settings → AI Assistant</strong> to unlock your coach.
      </div>
    </div>
  );

  // ── TOOLBAR ─────────────────────────────────────────────────────────────────

  const isEmpty = messages.length === 0;
  const showHome = isEmpty && stats.hasData;

  const TABS = [
    { id: 'main',     l: 'Coach' },
    { id: 'insights', l: 'Insights' },
    { id: 'history',  l: 'History' },
    { id: 'usage',    l: 'Usage' },
    { id: 'settings', l: 'Settings' },
  ];

  const TabBar = (
    <div style={{padding:'2px 14px 0',display:'flex',alignItems:'flex-end',gap:0,borderBottom:`0.5px solid ${C.lineS}`,background:C.bg0,flexShrink:0}}>
      <div style={{display:'flex',flex:1,gap:20}}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setView(t.id)} style={{
            background:'none',border:0,padding:'12px 0',cursor:'pointer',
            color: view===t.id ? C.ink1 : C.ink3,
            fontSize: 13, fontWeight: view===t.id ? 500 : 400,
            fontFamily: fontSans, letterSpacing:'-0.01em',
            position:'relative', WebkitTapHighlightColor:'transparent',
            minHeight: 44,
          }} aria-label={t.l} aria-current={view===t.id ? 'page' : undefined}>
            {t.l}
            {view===t.id && <div style={{position:'absolute',bottom:-0.5,left:-4,right:-4,height:1.8,background:C.amber,borderRadius:1.5}}/>}
          </button>
        ))}
      </div>
    </div>
  );

  // ── CHAT HEADER (active chat) ───────────────────────────────────────────────
  // Per v2 spec: when a chat is active the standard 4-tab Coach/History/About/Data
  // strip gives way to a focused chat header — back chevron, serif italic session
  // title (inferred from first user message), '<n> messages · <relative date>'
  // subtitle, and a "+ New" pill on the right that starts a fresh conversation.
  const currentTopic = inferTopic(messages);
  const headerSub = `${messages.length} message${messages.length === 1 ? '' : 's'} · ${relDate(Date.now())}`;
  const ChatHeader = (
    <div style={{padding:'10px 16px',display:'flex',alignItems:'center',gap:10,borderBottom:`0.5px solid ${C.lineS}`,background:C.bg0,flexShrink:0,minHeight:48}}>
      <button onClick={() => { try { hapticTap(); } catch {} setView("history"); }} aria-label="Back to history" style={{background:'none',border:0,color:C.ink3,cursor:'pointer',padding:'4px',display:'flex',alignItems:'center',flexShrink:0,WebkitTapHighlightColor:'transparent',minHeight:36,minWidth:36,justifyContent:'center'}}>{Ic.back(C.ink3)}</button>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontFamily:fontSerif,fontStyle:'italic',fontSize:14.5,color:C.ink1,letterSpacing:'-0.01em',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{currentTopic}</div>
        <div style={{fontFamily:fontMono,fontSize:8,letterSpacing:'0.12em',textTransform:'uppercase',color:C.ink4,marginTop:1}}>{headerSub}</div>
      </div>
      <button onClick={startNew} aria-label="New conversation" style={{
        background:C.amberD,border:`0.5px solid rgba(224,160,48,0.22)`,borderRadius:20,
        padding:'6px 11px',color:C.amber,fontSize:11.5,fontFamily:fontSans,
        cursor:'pointer',display:'flex',alignItems:'center',gap:4,flexShrink:0,
        WebkitTapHighlightColor:'transparent',
      }}>{Ic.plus(C.amber)}<span>New</span></button>
    </div>
  );

  // ── HISTORY VIEW ────────────────────────────────────────────────────────────

  // Group filtered conversations by their relative-date label so the list reads
  // as Today / Yesterday / Monday / 12 May etc. Order: most recent group first
  // (insertion order — `conversations` is already sorted by lastUpdatedAt desc).
  const historyFiltered = conversations.filter(c => {
    if (!historyQuery.trim()) return true;
    const q = historyQuery.toLowerCase();
    return (c.topic || '').toLowerCase().includes(q) || lastAssistantPreview(c.messages || []).toLowerCase().includes(q);
  });
  const historyGroups = (() => {
    const out = []; const seen = new Set();
    for (const c of historyFiltered) {
      const label = relDate(c.lastUpdatedAt || c.startedAt);
      if (!seen.has(label)) { seen.add(label); out.push({ label, items: [] }); }
      out[out.length - 1].items.push(c);
    }
    return out;
  })();

  const HistoryView = (
    <div className="haic-no-sb" ref={scrollRef} style={{flex:1,overflowY:"auto",overflowX:"hidden"}}>
      {/* Search + New row — v2 spec § History View header */}
      <div style={{display:'flex',alignItems:'center',gap:8,padding:'12px 16px 8px'}}>
        <div style={{flex:1,display:'flex',alignItems:'center',gap:8,background:C.bg2,border:`0.5px solid ${C.line}`,borderRadius:11,padding:'9px 12px'}}>
          <svg width="15" height="15" viewBox="0 0 20 20" fill="none" style={{flexShrink:0,color:C.ink4}}>
            <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M13 13l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          <input value={historyQuery} onChange={e => setHistoryQuery(e.target.value)} placeholder="Search conversations" style={{flex:1,background:'none',border:0,outline:'none',color:C.ink1,fontSize:13,fontFamily:fontSans,letterSpacing:'-0.01em',padding:0}}/>
          {historyQuery && (
            <button onClick={() => setHistoryQuery("")} aria-label="Clear search" style={{background:'none',border:0,color:C.ink4,fontSize:16,cursor:'pointer',padding:'0 4px',lineHeight:1}}>×</button>
          )}
        </div>
        <button onClick={startNew} aria-label="New conversation" style={{width:38,height:38,borderRadius:11,background:C.amberD,border:`0.5px solid rgba(224,160,48,0.30)`,color:C.amber,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,WebkitTapHighlightColor:'transparent'}}>
          {Ic.plus(C.amber)}
        </button>
      </div>

      {conversations.length === 0 ? (
        <div style={{padding:'40px 16px',textAlign:'center'}}>
          <div style={{display:'inline-flex'}}>{Ic.history(C.ink4)}</div>
          <div style={{fontFamily:fontSerif,fontStyle:'italic',fontSize:18,color:C.ink2,marginTop:12,marginBottom:6}}>No conversations yet</div>
          <div style={{fontSize:12.5,color:C.ink4,lineHeight:1.6,maxWidth:240,margin:'0 auto'}}>Your past chats with the coach will appear here.</div>
        </div>
      ) : historyFiltered.length === 0 ? (
        <div style={{padding:'30px 16px',textAlign:'center',fontSize:12.5,color:C.ink4}}>No matches for "{historyQuery}".</div>
      ) : (
        <div style={{padding:'4px 16px 16px'}}>
          {historyGroups.map((g, gi) => (
            <div key={gi} style={{marginTop: gi === 0 ? 8 : 18}}>
              <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.ink4,marginBottom:6}}>{g.label.toUpperCase()}</div>
              <div>
                {g.items.map(c => {
                  const isActive = c.id === currentId;
                  return (
                    <HayahSwipeRow
                      key={c.id}
                      onDelete={() => deleteConversation(c.id)}
                      onTap={() => resumeConversation(c)}
                      ariaLabel={`Conversation: ${c.topic} — tap to open, swipe left to delete`}
                    >
                      <div style={{
                        display:'flex',gap:12,alignItems:'flex-start',
                        padding: isActive ? '10px 12px' : '12px 0',
                        // Solid bg so the DELETE pad doesn't bleed through the row content while swiping.
                        background: isActive ? C.bg2 : (C.bg1 || C.bg || '#0a0c10'),
                        border: isActive ? `0.5px solid ${C.line}` : 'none',
                        borderBottom: isActive ? `0.5px solid ${C.line}` : `0.5px solid ${C.lineS}`,
                        borderRadius: isActive ? 12 : 0,
                        marginBottom: isActive ? 6 : 0,
                        cursor:'pointer',
                      }}>
                        {isActive ? (
                          <div style={{width:32,height:32,borderRadius:11,background:C.amberD,border:`0.5px solid rgba(224,160,48,0.30)`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1}}>
                            <CoachOrb size={20} C={C}/>
                          </div>
                        ) : (
                          <div style={{width:32,height:32,borderRadius:9,background:C.bg3,border:`0.5px solid ${C.line}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1,color:C.ink4}}>
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.3"/><path d="M10 6v4l2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
                          </div>
                        )}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
                            <div style={{flex:1,fontSize:13.5,fontWeight: isActive ? 500 : 400,color: isActive ? C.ink1 : C.ink2,letterSpacing:'-0.015em',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.topic}</div>
                            {isActive && <div style={{width:7,height:7,borderRadius:'50%',background:C.amber,boxShadow:`0 0 6px ${C.amber}`,flexShrink:0}}/>}
                          </div>
                          <div style={{fontSize:11.5,color:C.ink3,lineHeight:1.5,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{lastAssistantPreview(c.messages || [])}</div>
                          <div style={{fontFamily:fontMono,fontSize:7.5,letterSpacing:'0.1em',textTransform:'uppercase',color:C.ink4,marginTop:4}}>{(c.messages || []).length} messages</div>
                        </div>
                      </div>
                    </HayahSwipeRow>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── PROFILE VIEW ────────────────────────────────────────────────────────────

  const connected = Object.entries(access).filter(([_,v]) => v).map(([k]) => ({
    calories:'Calories', weight:'Body weight', bf:'Body composition', sleep:'Sleep', steps:'Steps', workout:'Workouts', fasting:'Fasting',
  }[k] || k));

  // ── AI Settings handlers ───────────────────────────────────────────────
  const handleSaveKey = (val) => {
    saveGeminiKey(val);
    setGeminiKey(val.trim());
    setTestState(null); setTestMsg("");
  };
  const handleModelChange = (id) => {
    saveGeminiModel(id);
    setGeminiModel(id);
    setTestState(null); setTestMsg("");
  };
  const handleTest = async () => {
    setTestState("loading"); setTestMsg("");
    const { status, ok, body } = await testGemini(geminiKey, geminiModel);
    if (ok) { setTestState("ok"); setTestMsg(`HTTP ${status} · OK`); }
    else {
      setTestState("error");
      let detail = (body || "").slice(0, 200);
      try { detail = JSON.parse(body)?.error?.message?.slice(0, 200) || detail; } catch {}
      setTestMsg(`HTTP ${status} · ${detail}`);
    }
  };
  const saveBio = (val) => {
    const trimmed = (val || "").slice(0, 2000);
    setBioText(trimmed);
    try { localStorage.setItem("hayahUserBio", trimmed); } catch {}
  };
  const saveRegion = (val) => {
    setRegionText(val);
    try { localStorage.setItem("hayahUserRegion", (val || "").trim() || "South Africa"); } catch {}
  };
  const saveFaithCtx = (val) => {
    setFaithCtx(val);
    try { localStorage.setItem("hayahFaithContext", val ? "true" : "false"); } catch {}
  };
  const halalOnly = !!(hayahPrefs?.calories?.halalOnlyFilter);
  const setHalalOnly = (val) => {
    updateHayahPrefs({ calories: { ...(hayahPrefs?.calories || {}), halalOnlyFilter: val } });
  };
  // voiceMode + autoSendCancelMs are declared earlier in this component
  // (the existing dictation path consumes them). The setters write back to
  // hayahPrefs.coach.
  const setVoiceMode = (val) => {
    updateHayahPrefs({ coach: { ...(hayahPrefs?.coach || {}), voiceMode: val } });
  };
  const setAutoSendMs = (val) => {
    updateHayahPrefs({ coach: { ...(hayahPrefs?.coach || {}), autoSendCancelMs: val } });
  };

  // ── USAGE VIEW ───────────────────────────────────────────────────────────
  const _fmt = (n) => Number(n || 0).toLocaleString();
  const _fmtUSD = (v) => "$" + (Number(v || 0)).toFixed(2);
  const usageSummary = usageState.summary;
  const usageFeatures = Object.entries(usageSummary?.byFeature || {})
    .map(([k, v]) => ({ key: k, ...v }))
    .sort((a, b) => (b.costUSD || 0) - (a.costUSD || 0));
  const usageSince = usageSummary?.sinceTs ? new Date(usageSummary.sinceTs).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—";
  // Apply the same calibration factor to per-feature rows + daily chart so
  // every visible dollar number is on the same scale as the headline total.
  const maxDayCost = applyCal(Math.max(0.0001, ...usageState.last7.map(d => d.costUSD || 0)));
  const last7Total = applyCal(usageState.last7.reduce((t, d) => t + (d.costUSD || 0), 0));
  const handleResetUsage = () => {
    if (typeof window !== "undefined" && !window.confirm("Wipe the running token totals and per-day history? (The baseline seed will need to be re-seeded manually if you've already used the AI a lot.)")) return;
    try { resetUsage(); } catch {}
    setUsageState({ summary: getUsageSummary(), last7: getLast7Days() });
  };

  const UsageView = (
    <div className="haic-no-sb" ref={scrollRef} style={{flex:1,overflowY:"auto",padding:"20px 22px 16px"}}>
      {/* TOTALS */}
      {(() => {
        const rawEstimate = Number(usageSummary?.totals?.costUSD || 0);
        const displayEstimate = applyCal(rawEstimate);
        const haveActual = actualSpend != null;
        const haveCal    = calibrationFactor != null;
        const delta = haveActual ? (actualSpend - displayEstimate) : 0;
        const deltaPct = haveActual && displayEstimate > 0 ? (delta / displayEstimate) * 100 : 0;
        const deltaColor = !haveActual ? C.ink4 : Math.abs(delta) < 0.005 ? C.ink3 : delta > 0 ? C.coral : C.teal;
        const deltaSign = delta > 0 ? "+" : delta < 0 ? "−" : "";
        const actualLabel = haveActual && actualSpendAt
          ? (() => { const _d = new Date(actualSpendAt); const _date = _d.toLocaleDateString(undefined, { day: "numeric", month: "short" }); const _hhmm = `${String(_d.getHours()).padStart(2,"0")}:${String(_d.getMinutes()).padStart(2,"0")}`; return `${_date} ${_hhmm}`; })()
          : null;
        const openActualPrompt = () => {
          setActualPromptValue(haveActual ? actualSpend.toFixed(2) : "");
          setActualPromptOpen(true);
        };
        const openAIStudio = () => {
          const url = "https://aistudio.google.com/u/1/spend?project=gen-lang-client-0297176676";
          if (typeof window !== "undefined") window.open(url, "_blank", "noopener,noreferrer");
        };
        return (
          <div style={{
            background:C.bg2,borderRadius:18,border:`0.5px solid ${C.line}`,
            padding:'20px 18px 14px',marginBottom:18,textAlign:'center',
          }}>
            <div style={{fontFamily:fontMono,fontSize:8,letterSpacing:'0.18em',textTransform:'uppercase',color:C.ink4,marginBottom:8}}>
              Total spend · {haveCal ? "calibrated" : "estimate"}
            </div>
            <div style={{fontFamily:fontSerif,fontStyle:'italic',fontSize:42,color:C.amber,letterSpacing:'-0.02em',lineHeight:1}}>
              {_fmtUSD(displayEstimate)}
            </div>
            <div style={{fontFamily:fontMono,fontSize:9.5,letterSpacing:'0.06em',color:C.ink3,marginTop:6}}>
              since {usageSince} · {_fmt(usageSummary?.totals?.calls)} calls
            </div>
            <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.06em',color:C.ink4,marginTop:3}}>
              {_fmt(usageSummary?.totals?.total)} tok — in {_fmt(usageSummary?.totals?.in)} · out {_fmt(usageSummary?.totals?.out)}
            </div>
            {haveCal && (
              <div style={{fontFamily:fontMono,fontSize:8,letterSpacing:'0.1em',color:C.ink4,marginTop:8,opacity:0.85}}>
                ×{calibrationFactor.toFixed(3)} factor · raw {_fmtUSD(rawEstimate)}
              </div>
            )}

            {/* Actual vs displayed row — tap to edit */}
            <div
              onClick={openActualPrompt}
              style={{
                marginTop:14,padding:'10px 12px',borderRadius:12,
                background:'rgba(255,255,255,0.03)',border:`0.5px solid ${C.lineS}`,
                cursor:'pointer',WebkitTapHighlightColor:'transparent',
                display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,textAlign:'left',
              }}
            >
              <div style={{minWidth:0}}>
                <div style={{fontFamily:fontMono,fontSize:8,letterSpacing:'0.16em',textTransform:'uppercase',color:C.ink4,marginBottom:3}}>
                  Actual {actualLabel ? `· ${actualLabel}` : ""}
                </div>
                {haveActual ? (
                  <div style={{display:'flex',alignItems:'baseline',gap:8,flexWrap:'wrap'}}>
                    <span style={{fontFamily:fontMono,fontSize:15,color:C.ink1,fontWeight:600,fontVariantNumeric:'tabular-nums'}}>
                      {_fmtUSD(actualSpend)}
                    </span>
                    <span style={{fontFamily:fontMono,fontSize:11,color:deltaColor,fontVariantNumeric:'tabular-nums'}}>
                      Δ {deltaSign}{_fmtUSD(Math.abs(delta))} ({deltaSign}{Math.abs(deltaPct).toFixed(1)}%)
                    </span>
                  </div>
                ) : (
                  <div style={{fontSize:11.5,color:C.ink3,letterSpacing:'-0.01em'}}>
                    Tap to record the real bill — every number on this tab will calibrate to it.
                  </div>
                )}
              </div>
              <div style={{fontFamily:fontMono,fontSize:9,color:C.ink4,letterSpacing:'0.08em',flexShrink:0}}>EDIT</div>
            </div>

            {/* View Gemini spend — opens AI Studio billing in browser */}
            <button
              onClick={openAIStudio}
              style={{
                marginTop:10,width:'100%',background:'transparent',
                border:`0.5px solid ${C.amber}55`,borderRadius:12,
                padding:'10px 12px',display:'flex',alignItems:'center',justifyContent:'space-between',
                cursor:'pointer',fontFamily:fontSans,WebkitTapHighlightColor:'transparent',
              }}
            >
              <div style={{textAlign:'left'}}>
                <div style={{fontSize:12.5,color:C.amber,letterSpacing:'-0.01em',fontWeight:600}}>View Gemini spend ↗</div>
                <div style={{fontSize:10.5,color:C.ink4,marginTop:1}}>Opens AI Studio billing — the real bill</div>
              </div>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{flexShrink:0,color:C.amber}}>
                <path d="M7 4h9v9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M16 4 5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        );
      })()}

      {/* BY FEATURE */}
      {usageFeatures.length > 0 && (
        <div style={{marginBottom:18}}>
          <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.ink4,marginBottom:10}}>By feature</div>
          <div style={{background:C.bg2,borderRadius:14,border:`0.5px solid ${C.line}`,overflow:'hidden'}}>
            {usageFeatures.map((f, i) => (
              <div key={f.key} style={{
                display:'flex',justifyContent:'space-between',alignItems:'center',
                padding:'10px 14px',
                borderBottom: i<usageFeatures.length-1 ? `0.5px solid ${C.lineS}` : 'none',
              }}>
                <div>
                  <div style={{fontSize:12.5,color:C.ink1,letterSpacing:'-0.01em'}}>{f.key}</div>
                  <div style={{fontFamily:fontMono,fontSize:8.5,color:C.ink4,marginTop:2,letterSpacing:'0.06em'}}>
                    {_fmt(f.total)} tok · {_fmt(f.calls)} calls
                  </div>
                </div>
                <div style={{fontFamily:fontMono,fontSize:13,color:C.amber,fontWeight:600,fontVariantNumeric:'tabular-nums'}}>
                  {_fmtUSD(applyCal(f.costUSD))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LAST 7 DAYS */}
      <div style={{marginBottom:18}}>
        <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.ink4,marginBottom:10}}>Last 7 days</div>
        <div style={{background:C.bg2,borderRadius:14,border:`0.5px solid ${C.line}`,padding:'14px 14px 10px'}}>
          <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',gap:5,height:54}}>
            {usageState.last7.map(d => {
              const v = applyCal(d.costUSD || 0);
              const h = v > 0 ? Math.max(4, Math.round((v / maxDayCost) * 50)) : 2;
              const label = d.date.slice(5);
              return (
                <div key={d.date} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                  <div style={{
                    width:'100%',height:h,borderRadius:3,
                    background: v > 0 ? C.amber : 'rgba(255,255,255,0.05)',
                    opacity: v > 0 ? 0.75 : 1,
                  }}/>
                  <div style={{fontFamily:fontMono,fontSize:7,color:C.ink4,letterSpacing:'0.06em'}}>{label}</div>
                </div>
              );
            })}
          </div>
          <div style={{display:'flex',justifyContent:'space-between',marginTop:10,paddingTop:8,borderTop:`0.5px solid ${C.lineS}`,fontFamily:fontMono,fontSize:9,color:C.ink4,letterSpacing:'0.06em'}}>
            <span>7-day <span style={{color:C.amber,fontWeight:600}}>{_fmtUSD(last7Total)}</span></span>
            <span>peak day {_fmtUSD(maxDayCost)}</span>
          </div>
        </div>
      </div>

      {/* RESET */}
      <button onClick={handleResetUsage} style={{
        width:'100%',background:'transparent',border:`0.5px solid ${C.lineS}`,
        borderRadius:14,padding:'12px 16px',color:C.coral,fontFamily:fontSans,fontSize:12,
        cursor:'pointer',WebkitTapHighlightColor:'transparent',letterSpacing:'-0.01em',
      }}>
        Reset counter
      </button>

      <div style={{height:16}}/>

      {actualPromptOpen && createPortal(
        <div
          onClick={() => setActualPromptOpen(false)}
          style={{
            position:'fixed',inset:0,zIndex:300,
            background:'rgba(0,0,0,0.55)',backdropFilter:'blur(6px)',WebkitBackdropFilter:'blur(6px)',
            display:'flex',alignItems:'center',justifyContent:'center',padding:'20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width:'100%',maxWidth:360,background:C.bg2,
              border:`0.5px solid ${C.line}`,borderRadius:20,padding:'20px 18px 16px',
              boxShadow:'0 30px 60px rgba(0,0,0,0.55)',fontFamily:fontSans,
            }}
          >
            <div style={{fontSize:15,fontWeight:700,color:C.ink1,letterSpacing:'-0.01em',marginBottom:6}}>
              Actual Gemini bill
            </div>
            <div style={{fontSize:12,color:C.ink3,lineHeight:1.55,marginBottom:14}}>
              Enter the latest number from Google Cloud Billing / AI Studio. The displayed cost on this tab will calibrate to it — Total Spend, By Feature and the 7-day chart all multiply by{" "}
              <span style={{fontFamily:fontMono}}>actual ÷ estimate</span>.
            </div>
            <div style={{
              display:'flex',alignItems:'center',gap:8,
              padding:'10px 12px',borderRadius:12,
              background:'rgba(255,255,255,0.04)',border:`0.5px solid ${C.line}`,marginBottom:14,
            }}>
              <span style={{fontFamily:fontMono,fontSize:14,color:C.ink3}}>$</span>
              <input
                type="text"
                inputMode="decimal"
                autoFocus
                value={actualPromptValue}
                onChange={(e) => setActualPromptValue(e.target.value.replace(/[^0-9.,]/g, ""))}
                placeholder="0.00"
                style={{
                  flex:1,background:'transparent',border:'none',outline:'none',
                  color:C.ink1,fontFamily:fontMono,fontSize:18,fontWeight:600,letterSpacing:'-0.01em',
                  padding:0,minWidth:0,
                }}
              />
            </div>
            <div style={{fontSize:10.5,color:C.ink4,lineHeight:1.5,marginBottom:14}}>
              Leave blank and tap Save to clear the calibration.
            </div>
            <div style={{display:'flex',gap:8}}>
              <button
                onClick={() => setActualPromptOpen(false)}
                style={{
                  flex:1,padding:'12px',borderRadius:12,
                  border:`0.5px solid ${C.lineS}`,background:'transparent',
                  color:C.ink3,fontFamily:fontSans,fontSize:13,fontWeight:600,
                  cursor:'pointer',WebkitTapHighlightColor:'transparent',
                }}
              >Cancel</button>
              <button
                onClick={() => {
                  const trimmed = actualPromptValue.trim();
                  if (!trimmed) {
                    clearActualSpend();
                  } else {
                    saveActualSpend(trimmed, Number(usageSummary?.totals?.costUSD || 0));
                  }
                  setActualPromptOpen(false);
                  try { hapticHayahSuccess(); } catch {}
                }}
                style={{
                  flex:1,padding:'12px',borderRadius:12,border:'none',
                  background:C.amber,color:'#0A0A0E',
                  fontFamily:fontSans,fontSize:13,fontWeight:700,letterSpacing:'-0.005em',
                  cursor:'pointer',WebkitTapHighlightColor:'transparent',
                }}
              >Save</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );

  // ── SETTINGS VIEW ───────────────────────────────────────────────────────────

  const toggleAccess = (k) => { try { hapticHayahToggle(); } catch {} setAccess(prev => ({ ...prev, [k]: !prev[k] })); };

  // Reusable toggle (matches the access-group toggle pattern below).
  const AcSwitch = ({ on, onChange, label }) => (
    <button onClick={() => onChange(!on)} aria-label={label} style={{
      width:46,height:27,borderRadius:14,border:'none',cursor:'pointer',flexShrink:0,
      background: on ? C.amber : C.bg4,
      position:'relative',transition:'background 0.2s',WebkitTapHighlightColor:'transparent',
    }}>
      <div style={{
        position:'absolute',top:3.5,left: on?22:3.5,width:20,height:20,
        borderRadius:'50%',background:'#fff',boxShadow:'0 1px 4px rgba(0,0,0,0.3)',
        transition:'left 0.2s',
      }}/>
    </button>
  );
  const AcSegmented = ({ value, options, onChange }) => (
    <div style={{display:'flex',gap:2,padding:2,background:C.bg4,borderRadius:10}}>
      {options.map(o => {
        const active = String(o.value) === String(value);
        return (
          <button key={o.value} onClick={() => onChange(o.value)} style={{
            padding:'5px 10px',borderRadius:8,border:'none',
            background: active ? C.amber : 'transparent',
            color: active ? '#1a0f00' : C.ink2,
            fontFamily: fontMono, fontSize:10,fontWeight:700,letterSpacing:'0.6px',
            cursor:'pointer',textTransform:'uppercase',transition:'background 0.15s',
          }}>{o.label}</button>
        );
      })}
    </div>
  );

  // Debug — show the latest ctxBlock the Coach received. Helps diagnose
  // "AI says no data but I have data" issues by exposing the actual JSON
  // shape the model is seeing. State is hoisted above the no-key early
  // return so the hook order is stable (Rules of Hooks).
  const openDebugCtx = () => {
    let ctx = "";
    let at = 0;
    let q = "";
    try {
      ctx = localStorage.getItem("sirat-debug-coach-ctx") || "";
      at = parseInt(localStorage.getItem("sirat-debug-coach-ctx-at") || "0", 10);
      q = localStorage.getItem("sirat-debug-coach-ctx-query") || "";
    } catch {}
    let parsed = null;
    try { parsed = ctx ? JSON.parse(ctx) : null; } catch {}
    // Read raw localStorage workouts so we can compare "what's on disk" vs
    // "what made it into context" — disambiguates "no data" from "data
    // filtered out".
    let rawWorkoutsCount = 0;
    let rawWorkoutsWithSets = 0;
    let rawWorkoutsLatest = null;
    try {
      const raw = JSON.parse(localStorage.getItem("sirat-workouts") || "[]");
      if (Array.isArray(raw)) {
        rawWorkoutsCount = raw.length;
        rawWorkoutsWithSets = raw.filter(w =>
          (w?.exercises || []).some(ex => (ex?.sets || []).length > 0)
        ).length;
        const sorted = [...raw].sort((a, b) =>
          (b?.endedAt || b?.startedAt || 0) - (a?.endedAt || a?.startedAt || 0)
        );
        const w = sorted[0];
        if (w) {
          rawWorkoutsLatest = {
            name: w.aiName || w.name || null,
            endedAt: w.endedAt ? new Date(w.endedAt).toLocaleString() : null,
            exerciseCount: (w.exercises || []).length,
            firstExercise: (w.exercises || [])[0] ? {
              name: w.exercises[0].name,
              setsCount: (w.exercises[0].sets || []).length,
              firstSet: (w.exercises[0].sets || [])[0] || null,
            } : null,
          };
        }
      }
    } catch {}
    // Pull a focused summary out of the raw context — what the user cares
    // about most when diagnosing exercise/data issues.
    const summary = parsed ? {
      capturedAt: at ? new Date(at).toLocaleString() : "never",
      lastUserQuery: q || "(unknown)",
      sectionsIncluded: Object.keys(parsed),
      rawStorage_sirat_workouts: {
        totalWorkouts: rawWorkoutsCount,
        workoutsWithAnySets: rawWorkoutsWithSets,
        latest: rawWorkoutsLatest,
      },
      exerciseInContext: parsed.exercise ? {
        recentWorkoutsCount: (parsed.exercise.recent || []).length,
        olderWorkoutsCount: (parsed.exercise.older || []).length,
        lastExercisesCount: (parsed.exercise.lastExercises || []).length,
        personalBestsCount: (parsed.exercise.personalBests || []).length,
        richSessions30d: parsed.exercise.richSessions30d,
        richSessionsAllTime: parsed.exercise.richSessionsAllTime,
        thisWeekCount: parsed.exercise.thisWeekCount,
        recentSample: (parsed.exercise.recent || []).slice(0, 3).map(w => ({
          name: w.name,
          date: w.date,
          loggedVia: w.loggedVia,
          exerciseCount: (w.exercises || []).length,
          firstExercise: (w.exercises || [])[0] ? {
            name: w.exercises[0].name,
            setsCount: (w.exercises[0].sets || []).length,
            firstSet: (w.exercises[0].sets || [])[0] || null,
          } : null,
        })),
      } : "EXERCISE SECTION MISSING FROM CONTEXT",
      todayInContext: parsed.today ? {
        date: parsed.today.date,
        cal: parsed.today.cal,
        protein: parsed.today.protein,
        carbs: parsed.today.carbs,
        fat: parsed.today.fat,
        foodsCount: (parsed.today.foods || []).length,
        foods: (parsed.today.foods || []).map(f => ({
          name: f.name || f.n,
          cal: f.cal || f.c,
          protein: f.protein || f.p,
          meal: f.meal || f.m,
        })),
      } : "TODAY SECTION MISSING FROM CONTEXT",
    } : null;
    setDebugCtxData({ summary, raw: ctx });
    setDebugCtxOpen(true);
  };

  const SettingsView = (
    <div className="haic-no-sb" ref={scrollRef} style={{flex:1,overflowY:"auto",padding:"16px 22px 16px"}}>
      {/* DEBUG — surface the latest AI context for diagnosis */}
      <div style={{marginBottom:22}}>
        <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.ink4,marginBottom:10}}>Debug</div>
        <button onClick={openDebugCtx} style={{
          width:'100%',padding:'13px 14px',borderRadius:14,
          background:C.bg2,border:`0.5px solid ${C.line}`,
          color:C.ink1,fontSize:13,fontFamily:fontSans,letterSpacing:'-0.01em',
          cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',
          WebkitTapHighlightColor:'transparent',
        }}>
          <div style={{textAlign:'left'}}>
            <div style={{marginBottom:2}}>🔍 Inspect AI Context</div>
            <div style={{fontSize:10.5,color:C.ink4,lineHeight:1.5}}>See exactly what data the Coach received on the last message.</div>
          </div>
          <span style={{color:C.ink4,fontSize:14}}>›</span>
        </button>
      </div>
      {/* API ACCESS */}
      <div style={{marginBottom:22}}>
        <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.ink4,marginBottom:10}}>API access</div>
        <div style={{background:C.bg2,borderRadius:16,border:`0.5px solid ${C.line}`,overflow:'hidden'}}>
          <div style={{padding:'13px 14px',borderBottom:`0.5px solid ${C.lineS}`}}>
            <div style={{fontSize:13,color:C.ink1,letterSpacing:'-0.01em',marginBottom:2}}>Gemini API key</div>
            <div style={{fontSize:10.5,color:C.ink4,lineHeight:1.5,marginBottom:8}}>From aistudio.google.com → Get API key. ~R0.01/month for light use after billing is enabled.</div>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              <input
                type="password" value={geminiKey} onChange={e => handleSaveKey(e.target.value)}
                placeholder="AIza…" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                style={{
                  flex:1,fontFamily:fontMono,fontSize:11,color:C.ink1,
                  background:C.bg3,border:`0.5px solid ${C.line}`,
                  borderRadius:8,padding:'8px 10px',outline:'none',minWidth:0,
                }}
              />
              <button onClick={handleTest} disabled={!geminiKey || testState === "loading"}
                style={{
                  padding:'8px 14px',borderRadius:8,fontFamily:fontMono,fontSize:10,
                  fontWeight:700,letterSpacing:'0.8px',textTransform:'uppercase',cursor:'pointer',
                  border:`0.5px solid ${C.amber}44`,background:`${C.amber}12`,color:C.amber,
                  opacity: !geminiKey || testState === "loading" ? 0.45 : 1,
                }}>{testState === "loading" ? "…" : "Test"}</button>
            </div>
            {testMsg && (
              <div style={{
                marginTop:8,fontFamily:fontMono,fontSize:9,
                color: testState === "ok" ? C.sage : testState === "error" ? C.coral : C.ink3,
                lineHeight:1.5,wordBreak:'break-word',whiteSpace:'pre-wrap',
              }}>{testMsg}</div>
            )}
          </div>
          <div style={{padding:'13px 14px'}}>
            <div style={{fontSize:13,color:C.ink1,letterSpacing:'-0.01em',marginBottom:2}}>Model</div>
            <div style={{fontSize:10.5,color:C.ink4,lineHeight:1.5,marginBottom:6}}>
              {GEMINI_MODELS.find(m => m.id === geminiModel)?.hint || ""}
            </div>
            <div style={{fontSize:10,color:C.ink4,lineHeight:1.5,marginBottom:8,fontStyle:'italic'}}>
              Only affects AI Coach (chat replies, photo chat, session summaries). Other Hayah AI features — food logging, workout generation, readiness, weekly recap, pattern insights — use fixed models per task.
            </div>
            <select value={geminiModel} onChange={e => handleModelChange(e.target.value)}
              style={{
                width:'100%',fontFamily:fontMono,fontSize:11,color:C.ink1,
                background:C.bg3,border:`0.5px solid ${C.line}`,
                borderRadius:8,padding:'8px 10px',outline:'none',cursor:'pointer',
              }}>
              {GEMINI_MODELS.map(m => (<option key={m.id} value={m.id}>{m.label}</option>))}
            </select>
          </div>
        </div>
      </div>

      {/* ABOUT ME */}
      <div style={{marginBottom:22}}>
        <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.ink4,marginBottom:10}}>About me</div>
        <div style={{background:C.bg2,borderRadius:16,border:`0.5px solid ${C.line}`,overflow:'hidden'}}>
          <div style={{padding:'13px 14px',borderBottom:`0.5px solid ${C.lineS}`}}>
            <div style={{fontSize:13,color:C.ink1,letterSpacing:'-0.01em',marginBottom:2}}>Bio</div>
            <div style={{fontSize:10.5,color:C.ink4,lineHeight:1.5,marginBottom:8}}>
              Training history, supplements, dietary preferences, injuries, goals beyond macros. Up to 2000 chars.
            </div>
            <textarea value={bioText} onChange={e => saveBio(e.target.value)} maxLength={2000} rows={6}
              placeholder="e.g. PT-trained 3x/week, creatine 5g/day, recomp goal, lower-back niggle…"
              style={{
                width:'100%',boxSizing:'border-box',
                fontFamily:fontMono,fontSize:11,color:C.ink1,lineHeight:1.5,
                background:C.bg3,border:`0.5px solid ${C.line}`,
                borderRadius:8,padding:'8px 10px',outline:'none',resize:'vertical',
              }}/>
            <div style={{fontFamily:fontMono,fontSize:8.5,color:C.ink4,marginTop:4,textAlign:'right',letterSpacing:'0.06em'}}>
              {bioText.length} / 2000
            </div>
          </div>
          <div style={{padding:'13px 14px',borderBottom:`0.5px solid ${C.lineS}`,display:'flex',alignItems:'center',gap:14}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,color:C.ink1,letterSpacing:'-0.01em',marginBottom:1}}>Region</div>
              <div style={{fontSize:10.5,color:C.ink4}}>Biases food-search regional knowledge.</div>
            </div>
            <input type="text" value={regionText} onChange={e => saveRegion(e.target.value)}
              onBlur={() => { if (!regionText.trim()) saveRegion("South Africa"); }}
              placeholder="South Africa"
              style={{
                width:140,padding:'7px 10px',textAlign:'right',
                background:C.bg3,border:`0.5px solid ${C.line}`,
                borderRadius:8,color:C.ink1,fontFamily:fontMono,fontSize:11,outline:'none',
              }}/>
          </div>
          <div style={{padding:'13px 14px',display:'flex',alignItems:'center',gap:14}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,color:C.ink1,letterSpacing:'-0.01em',marginBottom:1}}>Faith context</div>
              <div style={{fontSize:10.5,color:C.ink4,lineHeight:1.5}}>
                Surfaces Islamic context (Sunnah, fasting, halal) in Today insights, the fast-broken toast, and food prompts. Does NOT change the Coach voice — it stays clinical.
              </div>
            </div>
            <AcSwitch on={faithCtx} onChange={saveFaithCtx} label="Faith context" />
          </div>
        </div>
      </div>

      {/* COACH VOICE / DICTATION */}
      <div style={{marginBottom:22}}>
        <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.ink4,marginBottom:10}}>Coach</div>
        <div style={{background:C.bg2,borderRadius:16,border:`0.5px solid ${C.line}`,overflow:'hidden'}}>
          <div style={{padding:'13px 14px',borderBottom:`0.5px solid ${C.lineS}`,display:'flex',alignItems:'center',gap:14}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,color:C.ink1,letterSpacing:'-0.01em',marginBottom:1}}>Voice mode</div>
              <div style={{fontSize:10.5,color:C.ink4,lineHeight:1.5}}>
                Auto-send fires when you stop talking. Review drops dictated text into the input to edit first.
              </div>
            </div>
            <AcSegmented
              value={voiceMode} onChange={setVoiceMode}
              options={[{ value:"review", label:"Review" }, { value:"auto-send", label:"Auto" }]}
            />
          </div>
          <div style={{padding:'13px 14px',display:'flex',alignItems:'center',gap:14}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,color:C.ink1,letterSpacing:'-0.01em',marginBottom:1}}>Auto-send delay</div>
              <div style={{fontSize:10.5,color:C.ink4,lineHeight:1.5}}>
                Cancel-chip grace window before send fires.
              </div>
            </div>
            <AcSegmented
              value={autoSendCancelMs} onChange={v => setAutoSendMs(parseInt(v, 10) || 0)}
              options={[{ value:"0", label:"0s" }, { value:"1000", label:"1s" }, { value:"1500", label:"1.5s" }, { value:"2500", label:"2.5s" }]}
            />
          </div>
        </div>
      </div>

      {/* DIETARY */}
      <div style={{marginBottom:22}}>
        <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.ink4,marginBottom:10}}>Dietary</div>
        <div style={{background:C.bg2,borderRadius:16,border:`0.5px solid ${C.line}`,overflow:'hidden'}}>
          <div style={{padding:'13px 14px',display:'flex',alignItems:'center',gap:14}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,color:C.ink1,letterSpacing:'-0.01em',marginBottom:1}}>Halal-only filter</div>
              <div style={{fontSize:10.5,color:C.ink4,lineHeight:1.5}}>
                Applies to AI food search, photo analyser, meal parser, and redescribe. Non-halal items get flagged + a brief note.
              </div>
            </div>
            <AcSwitch on={halalOnly} onChange={setHalalOnly} label="Halal filter" />
          </div>
        </div>
      </div>

      {/* DATA SOURCES */}
      <div style={{fontSize:12,color:C.ink3,marginBottom:14,lineHeight:1.55}}>
        The coach only uses data sources you enable. Toggles take effect on your next message.
      </div>
      {ACCESS_GROUPS.map(g => (
        <div key={g.title} style={{marginBottom:22}}>
          <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.ink4,marginBottom:10}}>{g.title}</div>
          <div style={{background:C.bg2,borderRadius:16,border:`0.5px solid ${C.line}`,overflow:'hidden'}}>
            {g.items.map((it,i)=>(
              <div key={it.k} style={{
                display:'flex',alignItems:'center',gap:14,padding:'13px 16px',
                borderBottom: i<g.items.length-1 ? `0.5px solid ${C.lineS}` : 'none',
              }}>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,color:C.ink1,letterSpacing:'-0.015em',marginBottom:1}}>{it.l}</div>
                  <div style={{fontSize:11.5,color:C.ink4}}>{it.d}</div>
                </div>
                <AcSwitch on={!!access[it.k]} onChange={()=>toggleAccess(it.k)} label={`Toggle ${it.l}`} />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* PRIVACY */}
      <div style={{padding:'12px 16px',background:C.bg2,borderRadius:14,border:`0.5px solid ${C.line}`}}>
        <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.ink4,marginBottom:6}}>Privacy</div>
        <div style={{fontSize:12,color:C.ink3,lineHeight:1.7}}>
          Your health data stays on your device. Only what the coach needs for each message is sent to the model.
        </div>
      </div>
      <div style={{height:16}}/>

      {/* LAST CONTEXT DEBUG -- only shown when sirat-debug=1 in localStorage */}
      {(() => {
        let _dbgMode = false;
        try { _dbgMode = localStorage.getItem('sirat-debug') === '1'; } catch {}
        if (!_dbgMode) return null;
        const _raw = (() => { try { return localStorage.getItem('sirat-debug-coach-ctx') || ''; } catch { return ''; } })();
        const _query = (() => { try { return localStorage.getItem('sirat-debug-coach-ctx-query') || ''; } catch { return ''; } })();
        let _pretty = _raw;
        try {
          const _p = JSON.parse(_raw);
          const _today = _p && _p.today ? _p.today : null;
          _pretty = _today
            ? '=== today section ===\n' + JSON.stringify(_today, null, 2) + '\n\n=== full context ===\n' + JSON.stringify(_p, null, 2)
            : JSON.stringify(_p, null, 2);
        } catch {}
        return (
          <div style={{marginTop:8,marginBottom:8}}>
            <button
              onClick={() => setDebugCtxInlineOpen(o => !o)}
              style={{
                width:'100%',padding:'10px 14px',borderRadius:12,
                background:'#1a1200',border:'0.5px solid #5a3e00',
                color:'#ffb300',fontSize:11,fontFamily:'monospace',
                cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',
                WebkitTapHighlightColor:'transparent',textAlign:'left',
              }}>
              <span style={{letterSpacing:'0.12em',textTransform:'uppercase',fontSize:9}}>Last context debug</span>
              <span>{debugCtxInlineOpen ? '\u25b2' : '\u25bc'}</span>
            </button>
            {debugCtxInlineOpen && (
              <div style={{marginTop:6,background:'#0d0d0d',border:'0.5px solid #333',borderRadius:10,padding:'10px 12px'}}>
                <div style={{fontFamily:'monospace',fontSize:9,color:'#888',marginBottom:6,letterSpacing:'0.08em'}}>
                  {'QUERY: ' + (_query || '(none)')}
                </div>
                <div style={{
                  fontFamily:'monospace',fontSize:10,color:'#ccc',
                  whiteSpace:'pre-wrap',wordBreak:'break-word',lineHeight:1.5,
                  maxHeight:400,overflowY:'auto',
                }}>
                  {_pretty || 'No context captured yet. Send a Coach message first.'}
                </div>
                <button
                  onClick={() => { try { navigator.clipboard?.writeText?.(_raw || ''); } catch {} }}
                  style={{
                    marginTop:8,width:'100%',padding:'8px 12px',borderRadius:8,
                    border:'0.5px solid #444',background:'#1a1a1a',color:'#aaa',
                    fontSize:10,fontFamily:'monospace',cursor:'pointer',letterSpacing:'0.08em',
                    textTransform:'uppercase',
                  }}>
                  Copy full JSON
                </button>
              </div>
            )}
          </div>
        );
      })()}

    </div>
  );

  // ── MAIN (EMPTY / HOME / CHAT) ──────────────────────────────────────────────

  const sleepStr = stats.sleep > 0 ? `${Math.floor(stats.sleep/60)}h ${stats.sleep%60}m` : '—';
  const emptyTiles = [
    { l:'Calories', v: stats.cal ? stats.cal.toLocaleString() : '—',  s:`of ${stats.calGoal.toLocaleString()}`,  c: C.amber },
    { l:'Protein',  v: stats.prot ? `${stats.prot}g` : '—',            s:`of ${stats.protGoal}g`,                 c: C.sage  },
    { l:'Sleep',    v: sleepStr,                                       s:'last night',                            c: C.coral },
    { l:'Steps',    v: stats.steps ? stats.steps.toLocaleString() : '—', s:`of ${stats.stepGoal.toLocaleString()}`, c: C.ink2 },
  ];

  const ringPct = stats.calGoal ? Math.round((stats.cal / stats.calGoal) * 100) : 0;
  const ringExceeded = ringPct > 100;
  const ringFillPct = Math.min(100, ringPct);
  const ringR = 22, ringC = 2 * Math.PI * ringR;
  const ringDash = `${ringC * (ringFillPct/100)} ${ringC}`;
  const remaining = Math.max(0, stats.calGoal - stats.cal);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 5) return "Late night.";
    if (h < 12) return "Good morning.";
    if (h < 17) return "Good afternoon.";
    if (h < 21) return "Good evening.";
    return "Late tonight.";
  })();

  const homeHeadline = (() => {
    const n = insights.length;
    if (n === 0) return "Your data is looking quiet today.";
    if (n === 1) return "1 thing needs your attention today.";
    return `${n} things need your attention today.`;
  })();

  // iOS-native interactive back gesture — only starts within 25px of the
  // screen's left edge. The transform is applied to a WRAPPING container
  // around the chat body (NOT the composer — that's rendered outside the
  // transformed stack because `position: fixed` reparents to any ancestor
  // with a transform per CSS spec; without the split, the composer would
  // anchor to the moving wrapper instead of the viewport). The destination
  // view (HistoryView) is rendered as a parallaxing backdrop behind the
  // moving card. Matches iPhone's UINavigationController back gesture +
  // Messages/Claude-style stack.
  // chatSwipeBack hoisted above the no-key early return.
  // Body of the chat view — header + scrollable messages, no composer.
  const MainChat = (
    <>
      <div
        className="haic-no-sb"
        ref={scrollRef}
        onScroll={onChatScroll}
        onTouchStart={chatSwipeBack.onTouchStart}
        onTouchMove={chatSwipeBack.onTouchMove}
        onTouchEnd={chatSwipeBack.onTouchEnd}
        // touch-action: pan-y lets iOS WKWebView keep handling vertical
        // scroll natively while leaving horizontal pans to the swipe-back
        // hook. Without this, the touch handlers can swallow the entire
        // sequence and freeze the chat.
        style={{flex:1,minHeight:0,overflowY:"auto",padding:"14px 16px 96px",display:"flex",flexDirection:"column",gap:22,touchAction:"pan-y",WebkitOverflowScrolling:"touch"}}
      >

        {isEmpty && !showHome && (
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:24,padding:"8px 4px 0",animation:"haic-fade 0.3s ease"}}>
            <CoachOrb size={64} pulse={true} C={C}/>
            <div style={{textAlign:"center"}}>
              <div style={{fontFamily:fontSerif,fontStyle:'italic',fontSize:30,color:C.ink1,letterSpacing:'-0.02em',lineHeight:1.15,marginBottom:10}}>
                Your health,<br/>explained.
              </div>
              <div style={{fontSize:13.5,color:C.ink3,lineHeight:1.7,maxWidth:280,margin:'0 auto'}}>
                Ask anything. The coach reads your real data — calories, weight, sleep, protein — and gives direct answers.
              </div>
            </div>
            <div style={{width:'100%',background:C.bg2,borderRadius:18,padding:'18px 20px',border:`0.5px solid ${C.line}`,textAlign:'left'}}>
              <div style={{fontFamily:fontMono,fontSize:8,letterSpacing:'0.14em',textTransform:'uppercase',color:C.ink4,marginBottom:14}}>Your data today</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px 24px'}}>
                {emptyTiles.map(m => (
                  <div key={m.l}>
                    <div style={{fontFamily:fontMono,fontSize:7.5,letterSpacing:'0.1em',textTransform:'uppercase',color:C.ink4,marginBottom:4}}>{m.l}</div>
                    <div style={{fontFamily:fontSerif,fontStyle:'italic',fontSize:22,color:m.c,lineHeight:1}}>{m.v}</div>
                    <div style={{fontSize:11,color:C.ink4,marginTop:3}}>{m.s}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{width:'100%',display:'flex',flexDirection:'column',gap:8}}>
              {SUGGESTIONS.slice(0,3).map((q,i) => (
                <button key={i} onClick={() => send(q)} style={promptRow(C, fontSans)}>
                  <span style={{fontSize:13.5,color:C.ink2,letterSpacing:'-0.01em'}}>{q}</span>
                  {Ic.chevR(C.ink4)}
                </button>
              ))}
            </div>
            <div style={{width:'100%',display:'flex',flexWrap:'wrap',gap:7,justifyContent:'center'}}>
              {SUGGESTIONS.slice(3).map((q,i) => (
                <button key={i} onClick={() => send(q)} style={chip(C, fontSans)}>{q}</button>
              ))}
            </div>
          </div>
        )}

        {isEmpty && showHome && (
          <div style={{display:"flex",flexDirection:"column",gap:18,animation:"haic-fade 0.3s ease"}}>
            <div>
              <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.14em',textTransform:'uppercase',color:C.ink4,marginBottom:10}}>Ask</div>
              <div style={{display:'flex',gap:7,flexWrap:'wrap'}}>
                {askSuggestions.map((q,i) => (
                  <button key={i} onClick={() => send(q)} style={chip(C, fontSans)}>{q}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.map((msg, i) => msg.role === 'user' ? (
          // User bubble — amber-tinted (v2 spec). Max 78% width, right-aligned,
          // asymmetric border-radius nipped at the bottom-right corner toward the
          // sender, ink1 body text. When the user attached a photo, it renders
          // ABOVE the text inside the same bubble — 200px wide, 16:9 crop.
          <div key={i} style={{display:'flex',justifyContent:'flex-end',animation:'haic-msgIn 0.22s ease'}}>
            <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4,maxWidth:'78%'}}>
              <div style={{
                background:'rgba(224,160,48,0.10)',
                border:'0.5px solid rgba(224,160,48,0.18)',
                borderRadius:'18px 4px 18px 18px',padding: msg.image ? '8px 8px 10px' : '10px 14px',
              }}>
                {msg.image && (
                  <div style={{width:200,height:Math.round(200*9/16),borderRadius:12,overflow:'hidden',marginBottom: msg.text ? 8 : 0,background:C.bg3}}>
                    <img src={msg.image} alt="Meal photo" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
                  </div>
                )}
                {msg.text && (
                  <div style={{fontSize:14,color:C.ink1,lineHeight:1.65,letterSpacing:'-0.01em',whiteSpace:'pre-wrap',wordBreak:'break-word',fontFamily:fontSans, padding: msg.image ? '0 6px' : 0}}>{msg.text}</div>
                )}
              </div>
              {msg.text && !msg.image && !loading && !streaming && (
                <button onClick={() => editUserMessage(i)} aria-label="Edit message"
                  style={{background:'none',border:0,cursor:'pointer',color:C.ink4,padding:'2px 6px',fontSize:11,fontFamily:fontSans,display:'flex',alignItems:'center',gap:4,WebkitTapHighlightColor:'transparent'}}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M4 14l9-9 2 2-9 9H4v-2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>
                  Edit
                </button>
              )}
            </div>
          </div>
        ) : msg.kind === 'summary' ? (
          // Session summary card — distinct elevated artefact, NOT a chat bubble.
          // Full-width, bg3 elevated, amber-tinted border, mono "SESSION SUMMARY"
          // header. Body paragraphs split on blank lines; bullet lines (• …) and
          // the "Tomorrow:" action get their own treatment.
          <div key={i} style={{margin:'4px 0',background:C.bg3,border:`0.5px solid rgba(224,160,48,0.22)`,borderRadius:16,padding:'14px 16px 16px',animation:'haic-fadeUp 0.4s ease both'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
              <div style={{width:8,height:8,borderRadius:'50%',background:C.amber,boxShadow:`0 0 8px ${C.amber}66`,flexShrink:0}}/>
              <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.amber}}>Session Summary · {messages.filter(m => m.kind !== 'summary').length} messages</div>
            </div>
            {(msg.text || '').split(/\n\s*\n/).filter(Boolean).map((para, k) => {
              const lines = para.split('\n').filter(l => l.trim());
              const allBullets = lines.length && lines.every(l => /^\s*[•\-]/.test(l));
              const isAction = /^\s*Tomorrow:/i.test(para);
              if (allBullets) {
                return (
                  <ul key={k} style={{listStyle:'none',padding:0,margin:'0 0 10px',display:'flex',flexDirection:'column',gap:6}}>
                    {lines.map((l, li) => (
                      <li key={li} style={{fontSize:13,lineHeight:1.55,color:C.ink2,fontFamily:fontSans,letterSpacing:'-0.005em',paddingLeft:14,textIndent:-12,whiteSpace:'pre-wrap'}}>
                        <span style={{color:C.amber,marginRight:2}}>•</span>{l.replace(/^\s*[•\-]\s*/, '')}
                      </li>
                    ))}
                  </ul>
                );
              }
              if (isAction) {
                return (
                  <div key={k} style={{marginTop:8,padding:'10px 12px',background:`rgba(224,160,48,0.10)`,border:`0.5px solid rgba(224,160,48,0.22)`,borderRadius:10,fontFamily:fontSerif,fontStyle:'italic',fontSize:14,color:C.amberL,letterSpacing:'-0.01em',lineHeight:1.55}}>
                    {para.replace(/^\s*Tomorrow:\s*/i, '')}
                    <span style={{fontFamily:fontMono,fontStyle:'normal',fontSize:8.5,letterSpacing:'0.18em',color:C.amber,marginLeft:10,verticalAlign:'middle'}}>TOMORROW</span>
                  </div>
                );
              }
              return (
                <div key={k} style={{fontSize:13.5,lineHeight:1.65,color:C.ink2,fontFamily:fontSans,letterSpacing:'-0.005em',marginBottom:10,whiteSpace:'pre-wrap'}}>{para}</div>
              );
            })}
          </div>
        ) : (
          // AI message — NO bubble (v2 key design decision). Orb + "Hayah" italic
          // label + body, with copy/retry actions and follow-up suggestion chips
          // beneath. Bold spans in the body render in amberL.
          <div key={i} style={{animation:'haic-msgIn 0.25s ease'}}>
            <div style={{display:'flex',gap:10,alignItems:'flex-start'}}>
              <CoachOrb size={26} C={C}/>
              <div style={{flex:1,minWidth:0,paddingTop:1}}>
                <div style={{fontFamily:fontSerif,fontStyle:'italic',fontSize:11,color:C.ink4,marginBottom:6,letterSpacing:'0.01em'}}>Hayah</div>
                <div style={{fontSize:14,lineHeight:1.75,letterSpacing:'-0.01em',color:C.ink2,wordBreak:'break-word',fontFamily:fontSans}}>
                  <CoachMarkdown text={msg.text} C={C} fontSans={fontSans} fontSerif={fontSerif} fontMono={fontMono} boldColor={C.amberL}/>
                </div>
                <div style={{display:'flex',gap:2,marginTop:10,animation:'haic-actionsIn 0.3s ease 0.1s both'}}>
                  <button onClick={() => copyMessage(msg.text, i)} aria-label={copiedIdx === i ? "Copied" : "Copy"} style={{
                    display:'flex',alignItems:'center',gap:4,background:'none',border:0,cursor:'pointer',
                    color: copiedIdx === i ? C.sage : C.ink4,
                    padding:'5px 7px',borderRadius:7,fontSize:11,fontFamily:fontSans,letterSpacing:'-0.01em',
                    transition:'color 0.15s',WebkitTapHighlightColor:'transparent',
                  }}>
                    <svg width="13" height="13" viewBox="0 0 20 20" fill="none"><rect x="3" y="3" width="10" height="13" rx="2" stroke="currentColor" strokeWidth="1.4"/><path d="M7 2h8a2 2 0 0 1 2 2v10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                    <span>{copiedIdx === i ? 'Copied' : 'Copy'}</span>
                  </button>
                  {i === messages.length - 1 && (
                    <button onClick={retryLast} aria-label="Regenerate" style={{
                      display:'flex',alignItems:'center',gap:4,background:'none',border:0,cursor:'pointer',
                      color:C.ink4,padding:'5px 7px',borderRadius:7,fontSize:11,fontFamily:fontSans,letterSpacing:'-0.01em',
                      WebkitTapHighlightColor:'transparent',
                    }}>
                      <svg width="13" height="13" viewBox="0 0 20 20" fill="none"><path d="M4 10A6 6 0 1 1 10 16" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M3.5 6v4h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      <span>Regenerate</span>
                    </button>
                  )}
                  {msg.id && (
                    <>
                      <button onClick={() => rateMessage(msg.id, 'up')} aria-label="Helpful" style={{display:'flex',alignItems:'center',background:'none',border:0,cursor:'pointer',color: feedback[msg.id] === 'up' ? C.sage : C.ink4,padding:'5px 6px',borderRadius:7,transition:'color 0.15s',WebkitTapHighlightColor:'transparent'}}>
                        <svg width="13" height="13" viewBox="0 0 20 20" fill="none"><path d="M6 9v8H3V9h3zm0 0l3.5-6.5c.9.2 1.5 1 1.5 2V6h4.5a1.5 1.5 0 0 1 1.5 1.8l-1.2 6A2 2 0 0 1 16.3 17H6" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>
                      </button>
                      <button onClick={() => rateMessage(msg.id, 'down')} aria-label="Not helpful" style={{display:'flex',alignItems:'center',background:'none',border:0,cursor:'pointer',color: feedback[msg.id] === 'down' ? C.coral : C.ink4,padding:'5px 6px',borderRadius:7,transition:'color 0.15s',WebkitTapHighlightColor:'transparent'}}>
                        <svg width="13" height="13" viewBox="0 0 20 20" fill="none" style={{transform:'rotate(180deg)'}}><path d="M6 9v8H3V9h3zm0 0l3.5-6.5c.9.2 1.5 1 1.5 2V6h4.5a1.5 1.5 0 0 1 1.5 1.8l-1.2 6A2 2 0 0 1 16.3 17H6" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>
                      </button>
                    </>
                  )}
                  {/* Cost chip — small per-message indicator of what this
                      reply cost in USD plus the model + token totals.
                      Only renders when usage was reported by the call. */}
                  {msg.cost && (
                    <span
                      title={`${msg.cost.modelId} · ${msg.cost.inT} in + ${msg.cost.outT} out tokens`}
                      style={{
                        display:'inline-flex',alignItems:'center',gap:4,
                        marginLeft:'auto',padding:'5px 8px',borderRadius:7,
                        color:C.ink4,fontSize:10.5,fontFamily:fontMono,letterSpacing:'0.02em',
                        background:'rgba(255,255,255,0.025)',border:'1px solid rgba(255,255,255,0.05)',
                      }}
                    >
                      $ {msg.cost.usd < 0.0001 ? '<0.0001' : msg.cost.usd.toFixed(4)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {Array.isArray(msg.cards) && msg.cards.length > 0 && (
              <div style={{paddingLeft:36,marginTop:10,display:'flex',flexDirection:'column',gap:8}}>
                {msg.cards.map((card, k) => (
                  <div key={card.topic} style={{
                    position:'relative',overflow:'hidden',
                    background:C.bg1,border:`0.5px solid ${C.line}`,borderRadius:14,padding:'12px 14px',
                    animation:`haic-fadeUp 0.3s ease ${0.1 + k*0.1}s both`,
                  }}>
                    <div style={{position:'absolute',left:0,top:0,bottom:0,width:2,background:card.color,borderRadius:'0 1px 1px 0'}}/>
                    <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:8,gap:8}}>
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <div style={{width:6,height:6,borderRadius:'50%',background:card.color,flexShrink:0}}/>
                        <span style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:card.color}}>{card.label}</span>
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontFamily:fontSerif,fontStyle:'italic',fontSize:18,color:card.color,lineHeight:1,letterSpacing:'-0.02em'}}>
                          {card.topic === 'weight'
                            ? (card.current > 0 ? card.current.toFixed(1) : '—')
                            : card.topic === 'workout'
                              ? card.current
                              : (typeof card.current === 'number' && card.goal
                                  ? (card.topic === 'sleep' ? card.current.toFixed(1) : Math.round(card.current).toLocaleString())
                                  : '—')}
                        </div>
                        <div style={{fontFamily:fontMono,fontSize:7.5,letterSpacing:'0.06em',color:C.ink4,marginTop:2}}>
                          {card.goal && card.topic !== 'weight' ? `of ${card.topic === 'sleep' ? card.goal : card.goal.toLocaleString()} ${card.unit}` : card.unit}
                        </div>
                      </div>
                    </div>
                    <div style={{display:'flex',justifyContent:'flex-start',marginBottom:8}}>
                      <SparkBars data={card.data} color={card.color} goal={card.goal || null} h={26} barW={9} gp={4}/>
                    </div>
                    <div style={{fontSize:11.5,color:C.ink3,lineHeight:1.4,letterSpacing:'-0.005em'}}>{card.trend}</div>
                  </div>
                ))}
              </div>
            )}
            {Array.isArray(msg.appliedActions) && msg.appliedActions.length > 0 && (
              <div style={{ marginLeft: 36, marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {msg.appliedActions.map((a, k) => (
                  <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 700,
                    color: a.undone ? C.ink4 : C.teal, background: a.undone ? 'transparent' : `${C.teal}18`,
                    border: `1px solid ${a.undone ? C.line : `${C.teal}44`}`, borderRadius: 16, padding: '5px 11px' }}>
                    {a.label}
                    {a.undo && !a.undone && (
                      <button onClick={() => undoCoachLog(i, k)}
                        style={{ background: 'transparent', border: 'none', color: C.ink4, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: fontSans, textDecoration: 'underline', padding: 0 }}>
                        Undo
                      </button>
                    )}
                  </span>
                ))}
                {onOpenLog && msg.appliedActions.some(a => !a.undone) && (
                  <button onClick={() => { try { hapticTap(); } catch {} onOpenLog(); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: C.ink1,
                      background: C.bg3, border: `1px solid ${C.line}`, borderRadius: 16, padding: '5px 11px', cursor: 'pointer', fontFamily: fontSans }}>
                    📋 View log →
                  </button>
                )}
              </div>
            )}
            {Array.isArray(msg.actionButtons) && msg.actionButtons.length > 0 && (
              <div style={{ marginLeft: 36, marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {msg.actionButtons.map((b, k) => (
                  <button key={k} disabled={b._done} onClick={() => runActionButton(i, k)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 16,
                      background: b._done ? 'transparent' : `${C.teal}18`, border: `1px solid ${b._done ? C.line : `${C.teal}55`}`,
                      color: b._done ? C.ink4 : C.teal, fontSize: 12, fontWeight: 600, fontFamily: fontSans,
                      cursor: b._done ? 'default' : 'pointer', WebkitTapHighlightColor: 'transparent' }}>
                    {b._done ? '✓' : '＋'} {b.label}
                  </button>
                ))}
              </div>
            )}
            {msg.workout && Array.isArray(msg.workout.exercises_list) && msg.workout.exercises_list.length > 0 && (
              <div style={{ marginLeft: 36, marginTop: 10, background: C.bg3, border: `1px solid ${C.teal}44`, borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ padding: '11px 13px 8px' }}>
                  <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.teal, marginBottom: 4 }}>🤖 Coach workout</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.ink1 }}>{msg.workout.name}</div>
                  <div style={{ fontSize: 11, color: C.ink4, marginTop: 1 }}>{msg.workout.exercises_list.length} exercises{msg.workout.desc ? ` · ${msg.workout.desc}` : ''}</div>
                </div>
                <div style={{ padding: '0 13px 8px' }}>
                  {msg.workout.exercises_list.map((ex, k) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', borderTop: k === 0 ? 'none' : `0.5px solid ${C.line}` }}>
                      <span style={{ fontSize: 12.5, color: C.ink2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.name}</span>
                      <span style={{ fontSize: 11, color: C.ink4, fontFamily: fontMono, flexShrink: 0, marginLeft: 8 }}>
                        {ex.unit === 'min' && ex.defaultDurationMin
                          ? `${ex.defaultDurationMin} min`
                          : (ex.defaultWeight > 0 ? `${ex.defaultWeight}kg × ${ex.defaultReps}` : `BW × ${ex.defaultReps}`)}
                      </span>
                    </div>
                  ))}
                </div>
                <button onClick={() => openCoachWorkout(msg.workout)}
                  style={{ width: '100%', padding: '12px', border: 'none', background: C.teal, color: '#04201b', fontSize: 13.5, fontWeight: 900, fontFamily: fontSans, cursor: 'pointer', letterSpacing: '0.02em' }}>
                  ▶ Open in Exercise
                </button>
              </div>
            )}
            {Array.isArray(msg.followUps) && msg.followUps.length > 0 && (
              <div style={{paddingLeft:36,marginTop:10,display:'flex',flexWrap:'wrap',gap:6}}>
                {msg.followUps.map((q, k) => (
                  <button key={k} onClick={() => send(q)} style={{
                    background:C.bg3,border:`0.5px solid ${C.line}`,borderRadius:20,
                    padding:'7px 13px',color:C.ink2,fontSize:12,fontFamily:fontSans,
                    cursor:'pointer',letterSpacing:'-0.01em',lineHeight:1,
                    animation:`haic-chipIn 0.3s ease ${0.08 + k*0.07}s both`,
                    WebkitTapHighlightColor:'transparent',
                  }}>{q}</button>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Streaming AI message — virtual bubble while text is being revealed
            word-by-word. Mirrors the resting AI layout but shows an amber blinking
            cursor at the end and hides actions/follow-ups until commit. */}
        {streaming && (
          <div style={{animation:'haic-msgIn 0.2s ease'}}>
            <div style={{display:'flex',gap:10,alignItems:'flex-start'}}>
              <CoachOrb size={26} C={C}/>
              <div style={{flex:1,minWidth:0,paddingTop:1}}>
                <div style={{fontFamily:fontSerif,fontStyle:'italic',fontSize:11,color:C.ink4,marginBottom:6,letterSpacing:'0.01em'}}>Hayah</div>
                <div style={{fontSize:14,lineHeight:1.75,letterSpacing:'-0.01em',color:C.ink2,whiteSpace:'pre-wrap',wordBreak:'break-word',fontFamily:fontSans}}>
                  <BoldText text={streamText} boldColor={C.amberL}/>
                  <span style={{display:'inline-block',width:1.5,height:15,background:C.amber,verticalAlign:'text-bottom',marginLeft:2,borderRadius:1,animation:'haic-blink 0.8s step-end infinite'}}/>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Typing indicator — shown while we're waiting on Gemini, before the
            first stream word arrives. Replaces the old amber-dot pulse bubble. */}
        {loading && !streaming && (
          <div style={{display:'flex',gap:10,alignItems:'flex-start',animation:'haic-msgIn 0.2s ease'}}>
            <CoachOrb size={26} C={C}/>
            <div style={{display:'flex',gap:5,alignItems:'center',paddingTop:9}}>
              {[0,1,2].map(j => (
                <div key={j} style={{width:6,height:6,borderRadius:'50%',background:C.ink3,animation:`haic-pulseDot 1.2s ease-in-out ${j*0.18}s infinite`}}/>
              ))}
            </div>
          </div>
        )}

        {summaryError && (
          <div role="alert" style={{
            alignSelf:'center',margin:'8px 16px 0',padding:'10px 14px',
            background:'rgba(255,138,101,0.08)',border:'1px solid rgba(255,138,101,0.28)',
            borderRadius:12,color:'#ff8a65',fontSize:12.5,fontFamily:fontSans,letterSpacing:'-0.01em',
            lineHeight:1.4,maxWidth:520,animation:'haic-fadeUp 0.3s ease both',
          }}>
            Couldn't summarise — {summaryError}. Tap Summarise again to retry.
          </div>
        )}

        {!loading && !streaming && messages.length >= 6 && messages.length >= summarisedAt + 3 && (
          <button onClick={summariseSession} style={{
            alignSelf:'center',background:C.bg2,border:`0.5px solid ${C.line}`,borderRadius:14,
            padding:'12px 14px',margin:'8px 16px 0',cursor:'pointer',
            display:'flex',alignItems:'center',gap:8,
            color:C.ink2,fontSize:13,fontFamily:fontSans,letterSpacing:'-0.01em',
            WebkitTapHighlightColor:'transparent',
            animation:'haic-fadeUp 0.3s ease both',
          }}>
            <span style={{color:C.amber,display:'inline-flex'}}>{Ic.spark(C.amber)}</span>
            <span>Summarise this session</span>
          </button>
        )}

        <div ref={endRef}/>
      </div>
    </>
  );

  // Composer rendered OUTSIDE the swipe-back transformed stack — position:
  // fixed must anchor to the viewport, but a transformed ancestor would
  // hijack it. Keeping it as a top-level sibling of the layered stack
  // preserves the docked-above-tab-bar behaviour.
  const MainComposer = (
    <>
      <div style={{
        position:'fixed', left:0, right:0,
        // No keyboard: flush above the floating tab bar (pill top sits at
        // `max(0, env-24) + 84` from viewport bottom).
        // Keyboard open: tab bar is hidden, viewport has shrunk via WKWebView
        // resize, so bottom:0 puts us flush against the keyboard.
        bottom: kbOpen ? 0 : 'calc(84px + max(0px, env(safe-area-inset-bottom) - 24px))',
        padding:'8px 12px',
        background:C.bg0, borderTop:`0.5px solid ${C.lineS}`,
        zIndex:50,
        transition:'bottom 0.18s cubic-bezier(0.22,1,0.36,1)',
      }}>
        {!atBottom && view === 'main' && messages.length > 0 && (
          <div style={{position:'absolute', top:-46, left:0, right:0, display:'flex', justifyContent:'center', pointerEvents:'none'}}>
            <button onClick={scrollToLatest} aria-label="Jump to latest"
              style={{pointerEvents:'auto', width:36, height:36, borderRadius:'50%', background:C.bg3, border:`0.5px solid ${C.line}`, color:C.ink2, boxShadow:'0 4px 14px rgba(0,0,0,0.3)', display:'flex',alignItems:'center',justifyContent:'center', cursor:'pointer', WebkitTapHighlightColor:'transparent', animation:'haic-fadeUp 0.2s ease'}}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        )}
        {pendingImage && (
          <div style={{maxWidth:480, margin:'0 auto 6px', display:'flex',alignItems:'center',gap:10,background:C.bg2,border:`0.5px solid ${C.line}`,borderRadius:14,padding:'8px 10px'}}>
            <div style={{width:46,height:46,borderRadius:10,overflow:'hidden',background:C.bg3,flexShrink:0,position:'relative'}}>
              <img src={pendingImage.dataUrl} alt="Pending meal" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.18em',textTransform:'uppercase',color:C.amber}}>Photo attached</div>
              <div style={{fontSize:11.5,color:C.ink3,marginTop:2,letterSpacing:'-0.005em'}}>Tap send to analyse — typing is optional.</div>
            </div>
            <button onClick={() => setPendingImage(null)} aria-label="Remove photo" style={{
              width:28,height:28,borderRadius:'50%',background:C.bg3,border:`0.5px solid ${C.line}`,
              color:C.ink3,fontSize:16,lineHeight:1,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
              WebkitTapHighlightColor:'transparent',
            }}>×</button>
          </div>
        )}
        <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoInputChange} style={{display:'none'}}/>
        {/* Full-screen Voice Mode overlay (portaled). Receives the latest AI
            reply text + a flag so it can pace TTS against the streamed reply. */}
        <VoiceMode
          open={voiceModeOpen}
          onClose={() => setVoiceModeOpen(false)}
          onSend={(text) => send(text)}
          latestReply={(() => {
            if (streaming) return streamText || "";
            const lastAi = [...messages].reverse().find(m => m.role === "ai");
            return lastAi?.text || "";
          })()}
          replyPending={loading || streaming}
        />
        <div className={dictating ? "haic-composer-listening" : undefined} style={{maxWidth:480, margin:'0 auto', display:'flex',alignItems:'flex-end',gap:6,background:C.bg2,borderRadius:24,padding:'8px 8px 8px 12px',border:`1px solid ${(input.trim() || pendingImage) ? 'rgba(224,160,48,0.35)' : C.line}`,boxShadow: dictating ? '0 0 0 4px rgba(240,138,107,0.10)' : 'none',transition:'border-color 0.2s, box-shadow 0.2s'}}>
          <button
            onClick={pickMealPhoto}
            aria-label="Attach a meal photo"
            disabled={loading || streaming}
            style={{
              width:34,height:34,borderRadius:'50%',border:'none',background:'transparent',
              color: pendingImage ? C.amber : C.ink3,cursor: loading||streaming ? 'default' : 'pointer',
              display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
              transition:'color 0.15s',WebkitTapHighlightColor:'transparent',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M3.5 6.5h2L7 4.5h6L14.5 6.5h2A1.5 1.5 0 0 1 18 8v8.5A1.5 1.5 0 0 1 16.5 18h-13A1.5 1.5 0 0 1 2 16.5V8a1.5 1.5 0 0 1 1.5-1.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
              <circle cx="10" cy="12" r="3" stroke="currentColor" strokeWidth="1.3"/>
            </svg>
          </button>
          <textarea
            ref={inputRef}
            className="haic-ta"
            value={input}
            onChange={e => {
              setInput(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px";
            }}
            onKeyDown={e => { if (e.key==="Enter"&&!e.shiftKey){e.preventDefault();send(input);} }}
            placeholder={dictating ? "Listening… speak now" : "Ask your coach anything…"}
            rows={1}
            autoComplete="off"
            autoCorrect="on"
            autoCapitalize="sentences"
            spellCheck={true}
            style={{flex:1,background:'none',border:'none',outline:'none',resize:'none',overflow:'auto',
              color:C.ink1,fontSize:14,fontFamily:fontSans,lineHeight:1.45,letterSpacing:'-0.01em',
              minHeight:32,maxHeight:140,padding:'6px 0'}}
          />
          {isDictationAvailable() && (
            <button
              onClick={toggleDictation}
              aria-label={dictating ? "Stop dictation" : "Dictate"}
              style={{
                position:'relative',
                width: dictating ? 60 : 34, height:34, borderRadius:17, border:'none',
                background: dictating ? 'rgba(240,138,107,0.18)' : 'transparent',
                color: dictating ? '#f08a6b' : C.ink3,
                cursor:'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',gap:6,flexShrink:0,
                transition:'background 0.15s, color 0.15s, width 0.25s ease',WebkitTapHighlightColor:'transparent',
              }}
            >
              {Ic.mic('currentColor')}
              {dictating && (() => {
                // 3-bar oscillator. Each bar pulled from a slightly shifted slice
                // of the current level so they don't all peak together — gives a
                // natural waveform feel without needing FFT data.
                const lv = Math.max(0.06, micLevel);
                const heights = [lv * 0.7, lv * 1.0, lv * 0.55];
                return (
                  <span aria-hidden="true" style={{ display:'inline-flex', alignItems:'center', gap:2, height:16 }}>
                    {heights.map((h, i) => (
                      <span key={i} style={{
                        width:2, height: Math.max(2, h * 16),
                        background:'#f08a6b', borderRadius:1,
                        transition: 'height 80ms linear',
                      }} />
                    ))}
                  </span>
                );
              })()}
            </button>
          )}
          {isDictationAvailable() && (
            <button
              onClick={() => { try { hapticTap(); } catch {}; setVoiceModeOpen(true); }}
              aria-label="Open voice mode"
              style={{
                width:34, height:34, borderRadius:'50%', border:'none',
                background:'transparent', color: C.ink3, cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
                WebkitTapHighlightColor:'transparent', transition:'color 0.15s',
              }}
            >
              {/* Soundwave / audio-conversation icon */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M4 10v4M8 7v10M12 4v16M16 8v8M20 11v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </button>
          )}
          {(loading || streaming) ? (
            // While the coach is responding, the send button becomes a STOP
            // button (filled square, like Claude/ChatGPT) — tap to halt and
            // keep whatever was already written.
            <button
              onClick={stopGeneration}
              aria-label="Stop response"
              style={{
                width:34,height:34,borderRadius:'50%',border:'none',
                background:C.ink1,color:C.bg1,cursor:'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
                WebkitTapHighlightColor:'transparent',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><rect x="2" y="2" width="8" height="8" rx="1.5" fill="currentColor"/></svg>
            </button>
          ) : (
            <button
              onClick={() => send(input)}
              disabled={(!input.trim() && !pendingImage)}
              style={{
                width:34,height:34,borderRadius:'50%',border:'none',
                background:(input.trim() || pendingImage) ? C.amber : C.bg4,
                color:(input.trim() || pendingImage) ? '#000' : C.ink4,
                cursor:(input.trim() || pendingImage)?'pointer':'default',
                display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
                transition:'background 0.15s,color 0.15s',WebkitTapHighlightColor:'transparent',
              }}
              aria-label="Send"
            >{Ic.send('currentColor')}</button>
          )}
        </div>
        {/* Cancel-send countdown chip — shows during the post-dictation
            grace window. Tapping it keeps the text in the input. */}
        {pendingAutoSend && (() => {
          const remainMs = Math.max(0, pendingAutoSend.expiresAt - Date.now());
          const remainS = (remainMs / 1000).toFixed(remainMs < 1000 ? 1 : 0);
          const pct = autoSendCancelMs > 0 ? Math.max(0, Math.min(100, (remainMs / autoSendCancelMs) * 100)) : 0;
          return (
            <div style={{maxWidth:480, margin:'6px auto 0', display:'flex', justifyContent:'center'}}>
              <button
                onClick={() => { try { hapticTap(); } catch {}; cancelPendingAutoSend(); }}
                aria-label="Cancel auto-send"
                style={{
                  position:'relative', overflow:'hidden',
                  padding:'7px 14px', borderRadius:14,
                  background:'rgba(224,160,48,0.10)', border:'1px solid rgba(224,160,48,0.45)',
                  color: C.amber, fontFamily: fontSans, fontSize: 12, fontWeight: 600,
                  cursor:'pointer', letterSpacing:'-0.01em',
                  display:'flex', alignItems:'center', gap:8,
                  WebkitTapHighlightColor:'transparent',
                }}
              >
                <span>Sending in {remainS}s — tap to cancel</span>
                <span style={{ position:'absolute', left:0, bottom:0, height:2, width: pct + '%', background: C.amber, transition: 'width 0.1s linear' }} />
              </button>
            </div>
          );
        })()}
        {/* aria-live partial transcription announcer for VoiceOver users.
            Visually hidden but spoken as dictation text comes in. */}
        <div
          aria-live="polite"
          aria-atomic="true"
          style={{ position:'absolute', width:1, height:1, padding:0, margin:-1, overflow:'hidden', clip:'rect(0 0 0 0)', whiteSpace:'nowrap', border:0 }}
        >
          {dictating ? ('Listening: ' + (input || '')) : ''}
        </div>
        {dictError && (
          <div role="alert" style={{maxWidth:480, margin:'6px auto 0', display:'flex', flexDirection:'column', alignItems:'center', gap:6}}>
            <div style={{ fontSize:11, color:'#f08a6b', textAlign:'center', letterSpacing:'-0.01em' }}>{dictError}</div>
            {isPermissionError(dictError) && (
              <button
                onClick={() => { try { hapticTap(); } catch {}; openIOSSettings(); }}
                style={{
                  padding:'5px 12px', borderRadius:12,
                  border:'1px solid rgba(240,138,107,0.45)', background:'rgba(240,138,107,0.10)',
                  color:'#f08a6b', fontFamily: fontSans, fontSize:11, fontWeight:600, cursor:'pointer',
                  letterSpacing:'-0.01em', WebkitTapHighlightColor:'transparent',
                }}
              >
                Open Settings →
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",background:C.bg0,fontFamily:fontSans,overflowX:"hidden"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400;500&display=swap');
        .haic-no-sb::-webkit-scrollbar{display:none}.haic-no-sb{scrollbar-width:none}
        @keyframes haic-pulse{0%,100%{opacity:0.4}50%{opacity:1}}
        @keyframes haic-orb{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
        @keyframes haic-fade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
        @keyframes haic-msgIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes haic-pulseDot{0%,100%{opacity:0.2;transform:scale(0.7)}50%{opacity:1;transform:scale(1)}}
        @keyframes haic-blink{0%,49%{opacity:1}50%,100%{opacity:0}}
        @keyframes haic-chipIn{from{opacity:0;transform:translateY(5px) scale(0.95)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes haic-actionsIn{from{opacity:0}to{opacity:1}}
        @keyframes haic-fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes haic-sweep{
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        /* Animated rainbow-into-coral border sweep applied to the composer
           while the mic is listening. The border is a conic-style gradient
           painted as a 1px ring around the composer; transforms the static
           coral border into a moving cue without changing layout. */
        .haic-composer-listening{
          position: relative;
          background:
            linear-gradient(#0c0d10, #0c0d10) padding-box,
            linear-gradient(90deg,
              rgba(240,138,107,0.95),
              rgba(255,179,0,0.85),
              rgba(76,217,194,0.75),
              rgba(240,138,107,0.95)
            ) border-box !important;
          background-size: 200% 200%, 200% 200% !important;
          animation: haic-sweep 2.6s linear infinite;
          border-color: transparent !important;
        }
        @media (prefers-reduced-motion: reduce){
          .haic-composer-listening{ animation: none !important; }
        }
        .haic-ta::placeholder{color:${C.ink4}}
        .haic-menu-input::placeholder{color:${C.ink4}}
      `}</style>
      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{
          position:'fixed',inset:0,background:'rgba(0,0,0,0.72)',
          zIndex:2000,display:'flex',alignItems:'flex-end',justifyContent:'center',
          animation:'haic-fade 0.15s ease',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width:'100%',maxWidth:560,background:C.bg1,
            borderRadius:'22px 22px 0 0',
            padding:`18px 18px calc(18px + env(safe-area-inset-bottom))`,
            border:`0.5px solid ${C.line}`,borderBottom:'none',
            display:'flex',flexDirection:'column',gap:12,
            maxHeight:'88vh',
          }}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div>
                <div style={{fontFamily:fontSerif,fontStyle:'italic',fontSize:20,color:C.ink1,letterSpacing:'-0.01em'}}>Restaurant menu</div>
                <div style={{fontFamily:fontMono,fontSize:8.5,letterSpacing:'0.14em',textTransform:'uppercase',color:C.amber,marginTop:3}}>Find the best macro fit</div>
              </div>
              <button onClick={() => setMenuOpen(false)} aria-label="Close" style={{
                background:'none',border:0,color:C.ink3,fontSize:24,lineHeight:1,
                cursor:'pointer',padding:'4px 8px',WebkitTapHighlightColor:'transparent',
              }}>×</button>
            </div>
            <div style={{background:C.bg2,borderRadius:12,padding:'10px 12px',border:`0.5px solid ${C.line}`,fontFamily:fontMono,fontSize:10.5,letterSpacing:'0.04em',color:C.ink3}}>
              REMAINING TODAY · {quick.calRemaining.toLocaleString()} KCAL · {quick.protRemaining}G P · {quick.carbsRemaining}G C · {quick.fatRemaining}G F
            </div>
            <input
              className="haic-menu-input"
              value={menuRestaurant}
              onChange={e => setMenuRestaurant(e.target.value)}
              placeholder="Restaurant name (optional)"
              style={{
                background:C.bg2,border:`0.5px solid ${C.line}`,borderRadius:12,
                padding:'12px 14px',color:C.ink1,fontSize:14,fontFamily:fontSans,
                outline:'none',letterSpacing:'-0.01em',
              }}
            />
            <textarea
              className="haic-menu-input"
              value={menuText}
              onChange={e => setMenuText(e.target.value)}
              placeholder="Paste or type the menu items here — line breaks help. Include prices/descriptions if you have them."
              rows={9}
              style={{
                background:C.bg2,border:`0.5px solid ${C.line}`,borderRadius:12,
                padding:'12px 14px',color:C.ink1,fontSize:14,fontFamily:fontSans,
                outline:'none',resize:'none',lineHeight:1.5,letterSpacing:'-0.01em',
                minHeight:180,maxHeight:'40vh',
              }}
            />
            <button
              onClick={submitMenu}
              disabled={!menuText.trim() || loading}
              style={{
                background: menuText.trim() && !loading ? C.amber : C.bg3,
                color: menuText.trim() && !loading ? '#000' : C.ink4,
                border:0,borderRadius:14,padding:'14px 18px',
                fontSize:14,fontWeight:600,fontFamily:fontSans,letterSpacing:'-0.01em',
                cursor: menuText.trim() && !loading ? 'pointer' : 'default',
                WebkitTapHighlightColor:'transparent',
              }}
            >Analyse menu →</button>
          </div>
        </div>
      )}
      {view === "insights" && <>{TabBar}<HayahAIInsights /></>}
      {view === "main" && (() => {
        const dragging = chatSwipeBack.dragging || chatSwipeBack.dx > 0;
        const screenW = (typeof window !== "undefined" && window.innerWidth) || 400;
        // Backdrop parallax: previous view sits 30% offscreen-left when
        // not dragging, slides to 0 as the user pulls the foreground
        // right. Same easing iOS uses on UINavigationController.
        const backdropX = -screenW * 0.30 + chatSwipeBack.dx * 0.30;
        const headerForChat = !isEmpty ? ChatHeader : TabBar;
        return (
          <div style={{
            position: "relative",
            flex: 1,
            // min-height: 0 is critical — without it the nested flex:1 chain
            // doesn't propagate a definite height down to the scrollable, so
            // overflow:auto never engages and vertical scroll appears broken
            // even though the touch handlers are clean.
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* Backdrop — the destination view (History) with ITS top bar,
                so the entire screen reads as already-there when peeking
                behind the foreground card. Rendered only while dragging. */}
            {dragging && (
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  background: C.bg || "#0a0c10",
                  transform: `translateX(${backdropX}px)`,
                  willChange: "transform",
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              >
                {TabBar}
                {HistoryView}
              </div>
            )}
            {/* Foreground — chat header + scrollable body. The composer is
                rendered OUTSIDE this stack (see below) because position:
                fixed elements reparent to any transformed ancestor — putting
                the composer here would un-anchor it from the viewport. */}
            <div style={{
              position: "relative",
              zIndex: 1,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              background: C.bg || "#0a0c10",
              transform: `translateX(${chatSwipeBack.dx}px)`,
              transition: chatSwipeBack.dragging ? "none" : "transform 0.3s cubic-bezier(0.22,0.61,0.36,1)",
              willChange: "transform",
              boxShadow: chatSwipeBack.dx > 0 ? "-12px 0 32px rgba(0,0,0,0.55)" : "none",
            }}>
              {headerForChat}
              {MainChat}
            </div>
          </div>
        );
      })()}
      {/* Composer sits outside the transformed stack so position:fixed
          continues to anchor against the viewport. Only rendered when the
          chat view is active. */}
      {view === "main" && MainComposer}
      {view === "history" && <>{TabBar}{HistoryView}</>}
      {view === "usage" && <>{TabBar}{UsageView}</>}
      {view === "settings" && <>{TabBar}{SettingsView}</>}
      {debugCtxOpen && debugCtxData && (
        <div onClick={() => setDebugCtxOpen(false)} style={{
          position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:1000,
          display:'flex',alignItems:'flex-end',animation:'haic-fade 0.18s ease',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width:'100%',maxWidth:560,margin:'0 auto',
            background:C.bg1,borderRadius:'20px 20px 0 0',
            border:`0.5px solid ${C.line}`,borderBottom:0,
            padding:`16px 16px calc(20px + env(safe-area-inset-bottom))`,
            maxHeight:'88vh',display:'flex',flexDirection:'column',
          }}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
              <div>
                <div style={{fontFamily:fontMono,fontSize:9,letterSpacing:'0.18em',color:C.amber,textTransform:'uppercase'}}>Debug</div>
                <div style={{fontFamily:fontSerif,fontStyle:'italic',fontSize:18,color:C.ink1,marginTop:2}}>AI Context Inspector</div>
              </div>
              <button onClick={() => setDebugCtxOpen(false)} style={{background:'none',border:0,color:C.ink3,fontSize:24,cursor:'pointer',padding:'4px 8px'}}>×</button>
            </div>
            <div style={{flex:1,minHeight:0,overflowY:'auto',background:C.bg2,border:`0.5px solid ${C.line}`,borderRadius:12,padding:'12px 14px',fontFamily:fontMono,fontSize:11,color:C.ink2,whiteSpace:'pre-wrap',wordBreak:'break-word',lineHeight:1.55}}>
              {debugCtxData.summary
                ? JSON.stringify(debugCtxData.summary, null, 2)
                : "No context captured yet. Send a Coach message first, then re-open this inspector."}
            </div>
            <div style={{display:'flex',gap:8,marginTop:12}}>
              <button onClick={() => {
                try { navigator.clipboard?.writeText?.(debugCtxData.raw || ''); } catch {}
              }} style={{flex:1,padding:'10px 12px',borderRadius:10,border:`0.5px solid ${C.line}`,background:C.bg2,color:C.ink1,fontSize:12,fontFamily:fontSans,cursor:'pointer',fontWeight:600}}>Copy full JSON</button>
              <button onClick={() => setDebugCtxOpen(false)} style={{flex:1,padding:'10px 12px',borderRadius:10,border:'none',background:C.amber,color:'#000',fontSize:12,fontFamily:fontSans,cursor:'pointer',fontWeight:700}}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── STYLE HELPERS ─────────────────────────────────────────────────────────────

function iconBtn(C) {
  return {
    background:'none',border:0,padding:'6px 8px',cursor:'pointer',
    display:'flex',alignItems:'center',justifyContent:'center',
    color: C.ink3, WebkitTapHighlightColor:'transparent',
    borderRadius:8,
  };
}
function promptRow(C, fontSans) {
  return {
    background:'none',border:`0.5px solid ${C.line}`,borderRadius:14,
    padding:'13px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',
    cursor:'pointer',width:'100%',textAlign:'left',fontFamily:fontSans,
    WebkitTapHighlightColor:'transparent',
  };
}
function chip(C, fontSans) {
  return {
    background:'none',border:`0.5px solid ${C.line}`,borderRadius:20,
    padding:'8px 13px',color:C.ink2,fontSize:12,fontFamily:fontSans,cursor:'pointer',
    WebkitTapHighlightColor:'transparent',
  };
}
